import { useRef, useEffect, useState, useCallback, useMemo, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useEmblaCarousel from 'embla-carousel-react';
import {
  Shield, AlertTriangle, MapPin, Clock, Car, Users, X, ChevronLeft, ChevronRight, Camera, Flag,
  CheckCircle, FileText, History, PlusCircle, ArrowUpDown, Video, ArrowLeft
} from 'lucide-react';
import { useAlertSound } from '../hooks/useAlertSound';
import { cn, isValidImageUrl} from '../lib/utils';
import {
  effectiveWebPushEnabled,
  readNotifiedAlertIds,
  writeNotifiedAlertIds,
} from '../lib/browserNotifications';
import { Button } from '../components/ui/button';
import { POIDetailModal } from '../components/POIDetailModal';
import Header from '../components/layout/Header';
import { api, type AlertFromAPI, type POIFromAPI, type AccompliceFromAPI, type ClientAlert, type StoreData, edgeFetchWithFallback, buildEdgeImageSrc } from '../services/api';
import { useDemoStore } from '../store/demoStore';
import { useAuth } from '../context/AuthContext';
import StoreSelectorModal from '../components/StoreSelectorModal';
import { flushSync } from 'react-dom';
import { useModal } from '../context/ModalContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import GuardFooter from '../components/layout/GuardFooter';
import { useConnectivity } from '../hooks/useConnectivity';
import OfflineShield from '../components/OfflineShield';

// --- Protocol helper (unchanged) ---
function getRecommendedProtocol(poi: any, t: (key: string) => string): { level: number; text: string } {
  const isMostWanted = poi.isMostWanted || poi.crime_committed || poi.wanted_in;
  // SPECIAL CASE: Most Wanted
  if (isMostWanted) {
    return { level: 5, text: t('guard.protocolMostWanted') || '⚠️ CALL LAW ENFORCEMENT IMMEDIATELY ⚠️' };
  }
  const hasAccomplices = poi.accomplices && poi.accomplices.length > 0;
  const hasVehicle = poi.vehicle != null;
  const threatLevel = poi.threatLevel;

  if (threatLevel === 'High' && hasAccomplices && hasVehicle) {
    return { level: 4, text: t('guard.threatLevel4') };
  } else if (threatLevel === 'High' && hasAccomplices) {
    return { level: 3, text: t('guard.threatLevel3') };
  } else if (threatLevel === 'Medium' || (threatLevel === 'High' && !hasAccomplices)) {
    return { level: 2, text: t('guard.threatLevel2') };
  } else {
    return { level: 1, text: t('guard.threatLevel1') };
  }
}

// Helper: cosine similarity between two vectors
function cosineSimilarity(a: number[], b: number[]): number {
  let dot = 0, normA = 0, normB = 0;
  for (let i = 0; i < a.length; i++) {
    dot += a[i] * b[i];
    normA += a[i] * a[i];
    normB += b[i] * b[i];
  }
  if (normA === 0 || normB === 0) return 0;
  return dot / (Math.sqrt(normA) * Math.sqrt(normB));
}

// --- Extended alert type ---
interface GuardAlert extends AlertFromAPI {
  acknowledgedBy?: string;
  acknowledgedByName?: string;
  isAcknowledgedLocally?: boolean;
  store_name?: string | null;
  note?: string | null; 
  similarity?: number | null;
}

// Helper to resolve image URLs (same as in POIFormModal)
function resolveImageUrl(url: string | null | undefined): string {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/')) return `https://api.isentinel360.com${url}`;
  return `https://api.isentinel360.com/${url}`;
}

// --- Alert Card Component (unchanged) ---
function AlertCard({
  alertData,
  onAcknowledge,
  onComplete,
  onAccompliceClick,
}: {
  alertData: GuardAlert;
  onAcknowledge: (alertId: string) => Promise<void>;
  onComplete: (alertId: string, report: string) => Promise<void>;
  onAccompliceClick: (accomplice: AccompliceFromAPI, mainPoi: any) => void;
}) {
  const { t } = useTranslation();
  const { poi } = alertData;
  const storeName = (alertData as any).store_name;


  // state variables (acknowledge, incidentNote, etc.)
  const [isProcessing, setIsProcessing] = useState(false);
  const [isAcknowledging, setIsAcknowledging] = useState(false);
  const isAcknowledged = alertData.acknowledgedBy != null || alertData.status === 'acknowledged' || alertData.status === 'completed';
  const [incidentNote, setIncidentNote] = useState('');
  const [isCompleting, setIsCompleting] = useState(false);
  const { showAlert } = useModal();

  // flip state and effect
  const [isFlipped, setIsFlipped] = useState(false);
  const [showCapture, setShowCapture] = useState(false);

  const handleAcknowledge = async () => {
    setIsProcessing(true);
    setIsAcknowledging(true);
    try {
      await onAcknowledge(alertData.id);
    } finally {
      setIsAcknowledging(false);
      setIsProcessing(false);
    }
  };

  const handleCompleteClick = () => {
    setIsCompleting(true);
  };

  const handleSubmitComplete = async () => {
    if (incidentNote.trim().length < 20) {
      //window.alert(t('guard.minReportLength') || 'Incident report must be at least 20 characters');
      showAlert(t('guard.minReportLength') || 'Incident report must be at least 20 characters');
      return;
    }
    await onComplete(alertData.id, incidentNote);
  };

  const handleCancelComplete = () => {
    setIsCompleting(false);
    setIncidentNote('');
  };

  //const poi = alertData.poi; // may be null/undefined

    // ---------- Access Control alert (no POI) ----------
    if (!poi) {
      // Determine the alert title based on event type
      let alertTitle = t('guard.unauthorized');
      if (alertData.event_type === 'unauthorized_vehicle') {
        alertTitle = t('guard.unauthorizedVehicle') || 'Unauthorized Vehicle';
      } else if (alertData.event_type === 'admin_alert') {
        alertTitle = t('guard.operationalIssue') || 'Operational Issue';
      }

      return (
        <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl overflow-hidden">
          {/* Capture image */}
          <div className="aspect-video bg-gray-200 dark:bg-gray-800 relative">
            {alertData.event_type === 'admin_alert' ? (
                <img src="/images/opissue.jpeg" alt="Operational Issue" className="w-full h-full object-contain" />
              ) : alertData.capture_image_url ? (
                <img src={alertData.capture_image_url} alt="Alert" className="w-full h-full object-contain" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm">
                  No image available
                </div>
              )
            }
            <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white p-2 text-sm font-medium">
              {alertTitle}
            </div>
          </div>

          <div className="p-4 space-y-4">
            {/* Guard assigned banner (unchanged) */}
            {isAcknowledged && alertData.status === 'acknowledged' && (
              <div className="bg-blue-50 dark:bg-blue-950/30 text-blue-800 dark:text-blue-300 p-2 rounded flex items-center gap-2 text-sm">
                <CheckCircle className="w-4 h-4" />
                <span>{t('guard.alertAcknowledgedBy')} {alertData.acknowledgedByName || alertData.acknowledgedBy || t('guard.unknownGuard')}</span>
              </div>
            )}

            {/* Alert info */}
            <div className="text-center">
              <AlertTriangle className="w-10 h-10 text-red-600 mx-auto mb-2" />
              <p className="text-lg font-semibold">{alertTitle}</p>
              {/* Show note if present (e.g., for admin alerts) */}
              {alertData.note && (
                <p className="text-sm text-muted-foreground mt-1">{alertData.note}</p>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                {new Date(alertData.createdAt).toLocaleString()}
              </p>
              {alertData.camera_name && (
                <p className="text-xs text-muted-foreground mt-2 flex items-center justify-center gap-1">
                  <span>🎥</span> {alertData.camera_name}
                </p>
              )}
              {alertData.store_name && (
                <div className="text-xs text-muted-foreground mt-1">
                  📍 {alertData.store_name}
                </div>
              )}
            </div>

            {/* Action Buttons – identical to the Guardian version (unchanged) */}
            {alertData.status !== 'completed' && (
              <div className="space-y-2">
                {!isAcknowledged ? (
                  <Button onClick={handleAcknowledge} disabled={isAcknowledging} className="w-full h-auto min-h-[2.5rem] py-2 whitespace-normal break-words bg-red-600 hover:bg-red-700 text-white" variant="default">
                    {/* v0.4.9.4.9 — this label has to WRAP in German and Russian.
                        The Button's base class carries `whitespace-nowrap`, and two
                        Tailwind `whitespace-*` utilities in the same layer are
                        resolved by STYLESHEET order, not by the order they appear
                        in this attribute — so `whitespace-normal` on the Button
                        alone is not reliable. On a child span it governs that
                        span's own text and cannot lose the cascade.
                        `h-auto` lets the button grow to a second line. */}
                    {isAcknowledging ? t('common.loading') : (
                      <span className="whitespace-normal">{t('guard.takingAction')}</span>
                    )}
                  </Button>
                ) : (
                  !isCompleting ? (
                    <Button onClick={handleCompleteClick} className="w-full h-auto min-h-[2.5rem] py-2 whitespace-normal break-words bg-red-600 hover:bg-red-700 text-white">
                      {/* v0.4.9.4.9 — sibling of the takingAction fix. Same Button base
                          class with `whitespace-nowrap`, same fix on a child span. */}
                      <span className="whitespace-normal">{t('guard.actionCompleted')}</span>
                    </Button>
                  ) : (
                    <div className="space-y-2">
                      <label className="block text-sm font-medium">{t('guard.incidentReport')}</label>
                      <textarea
                        value={incidentNote}
                        onChange={(e) => setIncidentNote(e.target.value)}
                        placeholder={t('guard.reportPlaceholder')}
                        className="w-full p-2 border rounded-md text-sm bg-background"
                        rows={3}
                        minLength={20}
                      />
                      <div className="flex gap-2">
                        <Button onClick={handleSubmitComplete} className="flex-1">
                          <FileText className="w-4 h-4 mr-1" /> {t('guard.submitReport')}
                        </Button>
                        <Button variant="outline" onClick={handleCancelComplete}>
                          {t('common.cancel')}
                        </Button>
                      </div>
                    </div>
                  )
                )}
              </div>
            )}
          </div>
        </div>
      );
    }

    // ---------- Guardian alert (normal POI)  ----------
    const protocol = getRecommendedProtocol(poi, t);
    return (
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl overflow-hidden">

      {/* Overlay spinner */}
        {isProcessing && (
          <div className="absolute inset-0 z-10 bg-black/30 flex items-center justify-center">
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-lg flex items-center gap-3">
              <svg className="animate-spin h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span className="font-semibold">Processing...</span>
            </div>
          </div>
        )}

        {/* Main POI Image */}
        <div className="aspect-video bg-gray-200 dark:bg-gray-800 relative">
        {/* Flip button - top left */}
        <button
          onClick={() => setIsFlipped(!isFlipped)}
          className="absolute top-2 left-2 z-10 bg-black/60 text-white rounded-full p-2 hover:bg-black/80 transition-colors"
          title={isFlipped ? "Show POI photo" : "Show capture"}
        >
          <ArrowUpDown className="w-5 h-5" />
        </button>

        {/* Confidence badge - top right */}
        {alertData.similarity !== null && alertData.similarity !== undefined && (
          <div className="absolute top-2 right-2 z-10 bg-black/60 text-white rounded-full px-3 py-1 text-sm font-bold">
            {(alertData.similarity * 100).toFixed(1)}% match
          </div>
        )}

        {/* Image container with fade transition */}
        <div className="w-full h-full relative">
          {/* POI photo (front) */}
          <div className={`absolute inset-0 transition-opacity duration-500 ${isFlipped ? 'opacity-0' : 'opacity-100'}`}>
            {isValidImageUrl(poi.photoUrl) ? (
              <img src={poi.photoUrl} alt={poi.code} className="w-full h-full object-contain" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm">
                {t('admin.noImagesAvailable')}
              </div>
            )}
          </div>
          
          {/* Capture image (back) */}
          <div className={`absolute inset-0 transition-opacity duration-500 ${isFlipped ? 'opacity-100' : 'opacity-0'}`}>
            {alertData.capture_image_url ? (
              <img src={alertData.capture_image_url} alt="Capture" className="w-full h-full object-contain" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm">
                No capture image
              </div>
            )}
          </div>
        </div>

        {/* Flip hint - bottom center */}
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 text-white/60 text-xs bg-black/40 px-2 py-0.5 rounded">
          {isFlipped ? 'Showing capture' : 'Showing POI'}
        </div>
      </div>

        {/* Status indicator if already acknowledged */}
          {isAcknowledged && alertData.status === 'acknowledged' && (
            <div className="bg-blue-50 dark:bg-blue-950/30 text-blue-800 dark:text-blue-300 p-2 rounded flex items-center gap-2 text-sm">
              <CheckCircle className="w-4 h-4" />
              <span>{t('guard.alertAcknowledgedBy')} {alertData.acknowledgedByName || alertData.acknowledgedBy || t('guard.unknownGuard')}</span>
            </div>
          )}
          {storeName && <p className="text-sm font-medium text-center">{storeName}</p>}

        <div className="p-4 space-y-4">
          {/* POI Info */}
          <div>
            <h3 className="text-xl font-bold font-mono">{poi.code}</h3>
            {/* Display note if present */}
            {alertData.note && (
              <p className="text-sm text-muted-foreground mt-1">{alertData.note}</p>
            )}
            <div className="flex items-center gap-2 mt-2">
              <span className={cn(
                "px-2 py-1 rounded-full text-xs font-semibold",
                poi.threatLevel === 'High' && "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
                poi.threatLevel === 'Medium' && "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
                poi.threatLevel === 'Low' && "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
              )}>
                {t('guard.threat')}: {t(`threat.${(poi.threatLevel || 'low').toLowerCase()}`)}
              </span>
            </div>
          </div>

          {/* Location & Time */}
          <div className="space-y-1 text-sm">
            <div>
              <h3 className="font-semibold flex items-center gap-2">
                <MapPin className="w-4 h-4" /> {t('guard.lastSeen')}
              </h3>
              <p className="text-ms text-muted-foreground mb-1">
                <Clock className="w-4 h-4 inline mr-1 text-muted-foreground" />
                {new Date(alertData.createdAt).toLocaleString()}
              </p>
              {alertData.camera_name && (
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <span>🎥</span> {alertData.camera_name}
                </p>
              )}
            </div>
          </div>

          <p className="text-sm border-t pt-3">
            <span className="font-semibold">{t('guard.notes')}:</span> {poi.notes || t('common.none')}
          </p>

          {/* Accomplices */}
          {poi.accomplices && poi.accomplices.length > 0 && (
            <div>
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <Users className="w-4 h-4" />
                {t('guard.accomplices')}
              </h4>
              <div className="space-y-3">
                {poi.accomplices?.length > 0 && poi.accomplices.map(acc => (
                  <div
                    key={acc.id}
                    className="border rounded-lg p-3 bg-card flex gap-3 items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                    onClick={() => onAccompliceClick(acc, poi)}
                  >
                    {isValidImageUrl(acc.photoUrl) ? (
                    <img src={acc.photoUrl} alt={acc.code} className="w-16 h-16 rounded-full object-contain bg-muted" />
                      ) : (
                        <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center text-xs text-muted-foreground">
                          {t('admin.noImagesAvailable')}
                        </div>
                      )}
                    <div className="flex-1">
                      <p className="font-mono text-sm font-medium">{acc.code}</p>
                      <span className={cn(
                        "inline-block mt-1 px-2 py-0.5 rounded-full text-xs",
                        acc.threatLevel === 'High' && "bg-red-100 text-red-800",
                        acc.threatLevel === 'Medium' && "bg-yellow-100 text-yellow-800",
                        acc.threatLevel === 'Low' && "bg-blue-100 text-blue-800"
                      )}>
                        {t(`threat.${(acc.threatLevel || 'low').toLowerCase()}`)}
                      </span>
                      {poi.lastSeen && (
                        <p className="text-xs text-muted-foreground mb-2 mt-2">
                          {t('guard.lastSeen')}: {new Date(poi.lastSeen).toLocaleString()}
                        </p>
                      )}
                      {acc.notes && <p className="text-xs text-muted-foreground mt-1">{acc.notes}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Vehicle */}
          {poi.vehicle && (
            <div className="border-t pt-3">
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <Car className="w-4 h-4" />
                {t('guard.getawayVehicle')}
              </h4>
              {poi.vehicle.photoUrl && (
                isValidImageUrl(poi.vehicle.photoUrl) ? (
                  <img src={poi.vehicle.photoUrl} alt="Vehicle" className="w-full h-32 object-contain rounded-md mb-2 bg-muted" />
                ) : (
                  <div className="w-full h-32 rounded-md bg-muted flex items-center justify-center text-xs text-muted-foreground mb-2">
                    {t('admin.noImagesAvailable')}
                  </div>
                )
              )}
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <p className="text-muted-foreground">{t('guard.licensePlate')}</p>
                  <p className="font-mono font-bold">{poi.vehicle.licensePlate}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">{t('guard.makeModel')}</p>
                  <p>{poi.vehicle.make} {poi.vehicle.model}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">{t('guard.color')}</p>
                  <p>{poi.vehicle.color}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">{t('guard.location')}</p>
                  <p>{poi.vehicle.location || ""} </p>
                </div>
              </div>
            </div>
          )}

          {/* POI Edit History */}
          {poi.editHistory && poi.editHistory.length > 0 && (
            <details className="mt-4 border-t pt-4">
              <summary className="cursor-pointer font-semibold flex items-center gap-2 text-sm">
                <History className="w-4 h-4" /> {t('admin.editHistory')} ({poi.editHistory.length})
              </summary>
              <div className="mt-3 space-y-2 max-h-60 overflow-y-auto text-sm">
                {poi.editHistory.map((entry, idx) => (
                  <div key={idx} className="border-l-2 border-muted pl-3 py-1">
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>{entry.editedBy}</span>
                      <span>{new Date(entry.editedAt).toLocaleString()}</span>
                    </div>
                    <div className="mt-1">
                      <span className="font-medium">{entry.field}:</span>{' '}
                      <span className="line-through text-red-600">
                        {typeof entry.oldValue === 'object' ? JSON.stringify(entry.oldValue) : String(entry.oldValue)}
                      </span>
                      {' → '}
                      <span className="text-green-600">
                        {typeof entry.newValue === 'object' ? JSON.stringify(entry.newValue) : String(entry.newValue)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </details>
          )}

          {/* Protocol */}
          <div className={cn(
            "rounded-lg p-3 border-2 text-sm",
            protocol.level === 5 && "bg-red-700 border-red-900 text-white font-bold animate-pulse", // Special MW style
            protocol.level === 4 && "bg-red-100 border-red-500 dark:bg-red-950/30",
            protocol.level === 3 && "bg-orange-100 border-orange-500 dark:bg-orange-950/30",
            protocol.level === 2 && "bg-yellow-100 border-yellow-500 dark:bg-yellow-950/30",
            protocol.level === 1 && "bg-blue-100 border-blue-500 dark:bg-blue-950/30"
          )}>
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="w-4 h-4" />
              <h4 className="font-bold">{protocol.level === 5 ? '🚨 EMERGENCY' : t('guard.protocol') + ' ' + protocol.level}</h4>
            </div>
            <p className={cn("text-xs", protocol.level === 5 && "font-bold text-center text-lg")}>
              {protocol.text}
            </p>
          </div>

          {/* Action Buttons */}
          {alertData.status !== 'completed' && (
            <div className="space-y-2">
              {!isAcknowledged ? (
                <Button onClick={handleAcknowledge} disabled={isAcknowledging} className="w-full h-auto min-h-[2.5rem] py-2 whitespace-normal break-words bg-red-600 hover:bg-red-700 text-white" variant="default">
                  {/* v0.4.9.4.9 — see the note on the other takingAction button. */}
                  {isAcknowledging ? t('common.loading') : (
                    <span className="whitespace-normal">{t('guard.takingAction')}</span>
                  )}
                </Button>
              ) : (
                !isCompleting ? (
                  <Button onClick={handleCompleteClick} className="w-full h-auto min-h-[2.5rem] py-2 whitespace-normal break-words bg-red-600 hover:bg-red-700 text-white">
                    {/* v0.4.9.4.9 — see the note on the other actionCompleted button. */}
                    <span className="whitespace-normal">{t('guard.actionCompleted')}</span>
                  </Button>
                ) : (
                  <div className="space-y-2">
                    <label className="block text-sm font-medium">{t('guard.incidentReport')}</label>
                    <textarea
                      value={incidentNote}
                      onChange={(e) => setIncidentNote(e.target.value)}
                      placeholder={t('guard.reportPlaceholder')}
                      className="w-full p-2 border rounded-md text-sm bg-background"
                      rows={3}
                      minLength={20}
                    />
                    <div className="flex gap-2">
                      <Button onClick={handleSubmitComplete} className="flex-1">
                        <FileText className="w-4 h-4 mr-1" /> {t('guard.submitReport')}
                      </Button>
                      <Button variant="outline" onClick={handleCancelComplete}>
                        {t('common.cancel')}
                      </Button>
                    </div>
                  </div>
                )
              )}
            </div>
          )}
        </div>
      </div>
    );
}

// --- Main GuardDashboard Component ---
/**
 * Two edge fetch strategies, because they answer different questions.
 *
 * edgeFetchOk   — keep trying rungs until one answers res.ok. Used for the
 *                 capture list, /ping, /details and /frame. If the store's
 *                 HTTPS hostname resolves but its proxy answers 404/502 we
 *                 must still fall back to the IP, or the grid renders
 *                 nothing. A helper that returns on ANY response never
 *                 reaches the IP rung — that is what broke the gallery on
 *                 both desktop and mobile.
 * edgeFetchAny  — return the FIRST response, ok or not. Used only where a
 *                 non-ok body is the message we must show (/capture-crop's
 *                 "that crop still contains more than one face").
 *
 * Identical to the pair in AuthorizedManagers.tsx.
 */
async function edgeFetchOk(
  opts: { hostname?: string | null; edgeIp?: string; edgePort?: number },
  path: string,
  init: RequestInit = {}
) {
  const urls: string[] = [];
  if (opts.hostname) urls.push(`https://${opts.hostname}${path}`);
  if (opts.edgeIp) urls.push(`https://${opts.edgeIp}${path}`);
  if (opts.edgeIp && opts.edgePort) urls.push(`http://${opts.edgeIp}:${opts.edgePort}${path}`);

  let last: Response | null = null;
  for (const url of urls) {
    try {
      const res = await fetch(url, init);
      if (res.ok) return res;
      last = res;
    } catch {
      // unreachable rung
    }
  }
  if (last) return last;
  throw new Error('The edge is unreachable from this device.');
}

async function edgeFetchAny(
  opts: { hostname?: string | null; edgeIp?: string; edgePort?: number },
  path: string,
  init: RequestInit = {}
) {
  const urls: string[] = [];
  if (opts.hostname) urls.push(`https://${opts.hostname}${path}`);
  if (opts.edgeIp) urls.push(`https://${opts.edgeIp}${path}`);
  if (opts.edgeIp && opts.edgePort) urls.push(`http://${opts.edgeIp}:${opts.edgePort}${path}`);

  for (const url of urls) {
    try {
      return await fetch(url, init);
    } catch {
      // unreachable rung
    }
  }
  throw new Error('The edge is unreachable from this device.');
}

export default function GuardDashboard() {
  const { t } = useTranslation();
  const { isOnline } = useConnectivity();
  const { user, activeStoreId, setActiveStoreId } = useAuth();
  const { showAlert } = useModal();
  const [activeStoreFull, setActiveStoreFull] = useState<StoreData | null>(null);
  const activeStoreName = useMemo(() => {
    if (!user?.stores || !activeStoreId) return null;
    const store = user.stores.find(s => s.store_id === activeStoreId);
    return store ? store.name : null;
  }, [user, activeStoreId]);
  const ip = activeStoreFull?.edge_server_ip;
  const port = activeStoreFull?.edge_server_port || 3002;
  const isDemoUser = user?.email === 'admin@isentinel360.com' || user?.email === 'guard@isentinel360.com';
  // If user has multiple stores and no active store selected, show the modal
  // removing since we have already a useEffect for this
  /*
  if (
    user &&
    user.stores &&
    user.stores.length > 1 &&
    !activeStoreId // &&
    //user.role !== 'cadmin' &&
    //user.role !== 'superadmin'
  ) {
    return <StoreSelectorModal />;
  }
  */

  const isDemoMode = useDemoStore(state => state.isDemoMode);
  //console.log('🔍 Demo mode status:', isDemoMode);
  //console.log('🔍 User email:', user?.email);
  const demoAlerts = useDemoStore(state => state.activeAlerts);
  const demoTriggerAlert = useDemoStore(state => state.triggerAlert);
  const demoClearAllAlerts = useDemoStore(state => state.clearAllAlerts);
  const demoAcknowledge = useDemoStore(state => state.acknowledgeAlert);
  const demoRecentFaces = useDemoStore(state => state.recentFaces);
  const demoFlagFace = useDemoStore(state => state.flagFaceAsPOI);

  const [alerts, setAlerts] = useState<GuardAlert[]>([]);
  const [clientAlerts, setClientAlerts] = useState<ClientAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'alerts' | 'faces'>('alerts');
  const [selectedPoiForModal, setSelectedPoiForModal] = useState<POIFromAPI | null>(null);
  const [showAcknowledgedStack, setShowAcknowledgedStack] = useState(false);
  const [carouselError, setCarouselError] = useState(false);

  /**
 * Local calendar day as YYYY-MM-DD.
 *
 * Same helper as AuthorizedManagers.tsx uses for its picker, on purpose: the
 * two screens must agree on what "today" means or a guard sees different
 * results depending on which tab they are on. A UTC day string and a local
 * hour side by side is the worst of both - it looks like UTC on screen and
 * mis-filters at the day boundary.
 */
function localDayKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// --- Face filter state: default to last 10 minutes ---
  const now = new Date();
  // LOCAL day, not toISOString(). The hour/minute filters beside this one
  // were always local (getHours/getMinutes), so mixing a UTC date with a local
  // hour made the Date box disagree with everything else on screen — and past
  // local midnight it silently hid that morning's captures, because a photo
  // taken at 00:30 on the 7th still carries the 6th as its UTC date.
  const [faceFilterDate, setFaceFilterDate] = useState<string>(localDayKey(now));
  const [faceFilterHour, setFaceFilterHour] = useState<number | null>(now.getHours());
  const [faceFilterMinBlock, setFaceFilterMinBlock] = useState<number | null>(Math.floor(now.getMinutes() / 10));
  const [faceFilterType, setFaceFilterType] = useState<'all' | 'person' | 'vehicle'>('all');

  // --- Local captures state ---
  const [localCaptures, setLocalCaptures] = useState<any[]>([]);
  const [isLocalNetwork, setIsLocalNetwork] = useState(false);
  const [showLocalCaptures, setShowLocalCaptures] = useState(false);

  // --- Add to POI modal state ---
  const [addToPoiModal, setAddToPoiModal] = useState<{ capture: any; embedding: number[]; photoUrl: string } | null>(null);
  const [poiSearchQuery, setPoiSearchQuery] = useState('');
  const [poiSearchResults, setPoiSearchResults] = useState<POIFromAPI[]>([]);
  const [searchingPois, setSearchingPois] = useState(false);

  // --- Add to Authorized Person (Guard) ---
  // A guard may ADD AN IMAGE to an existing Authorized Person, but may not
  // CREATE one — that stays an Admin action, so this only ever picks a person
  // who already exists.
  const [addToAuthModal, setAddToAuthModal] = useState<{ capture: any; photoUrl: string; faceCount: number } | null>(null);
  const [authSearchQuery, setAuthSearchQuery] = useState('');
  const [authSearchResults, setAuthSearchResults] = useState<any[]>([]);
  const [addingToAuth, setAddingToAuth] = useState(false);

  // --- Add to Authorized Person functions ---
  const handleAddToAuthClick = async (capture: any) => {
    if (!activeStoreId) { showAlert('No store selected.'); return; }
    try {
      const edgeOpts = { hostname: edgeHostname, edgeIp, edgePort };
      const persons = await api.getAuthorizedPersons(activeStoreId);
      if (!persons || persons.length === 0) {
        showAlert('This store has no Authorized Persons yet. Ask an admin to add them first.');
        return;
      }
      setAddToAuthModal({
        capture,
        photoUrl: buildEdgeImageSrc(edgeOpts, `/captures/${capture.id}`).primary,
        faceCount: 1,
      });
      setAuthSearchResults(persons);
      setAuthSearchQuery('');
    } catch (err: any) {
      showAlert('Failed to load Authorized Persons: ' + err.message);
    }
  };

  const handleAddToAuthConfirm = async (personId: string) => {
    if (!addToAuthModal || !activeStoreId) return;
    const { capture } = addToAuthModal;
    setAddingToAuth(true);
    try {
      const edgeOpts = { hostname: edgeHostname, edgeIp, edgePort };

      // Which face? The edge stored the bbox of the face that raised this
      // alert, so we crop THAT one. Re-running detection here would rank by
      // confidence and could hand back a different person standing nearby.
      const detRes = await edgeFetchWithFallback(edgeOpts, `/captures/${capture.id}/details`);
      if (!detRes || !detRes.ok) throw new Error('Failed to fetch capture details');
      const details = await detRes.json();

      let bbox = Array.isArray(details.bbox) ? details.bbox : null;
      if (!bbox) {
        // Older capture with no stored box: accept only an UNAMBIGUOUS single
        // face. With two people we must not guess which one this alert was about.
        const fd = await edgeFetchWithFallback(edgeOpts, '/detect-faces', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ capture_id: capture.id }),
        });
        if (!fd || !fd.ok) throw new Error('Could not read the faces in this capture.');
        const { faces } = await fd.json();
        if (!faces || faces.length === 0) throw new Error('No face found in this capture.');
        if (faces.length > 1) {
          throw new Error(
            'This capture has more than one person and no stored face box, so the right one ' +
            'cannot be determined. Add the image from the Edge gallery instead.'
          );
        }
        bbox = faces[0].bbox;
      }

      // Crop on the edge, at natural size and aspect. Uploading the whole
      // frame would let the server re-embed whichever face happens to score
      // highest, which is how a roster ends up holding the wrong person.
      // edgeFetchAny, not edgeFetchWithFallback: we need the real response so
      // a 422 ("that crop still contains more than one face") reaches the
      // guard instead of collapsing to null.
      const cropRes = await edgeFetchAny(edgeOpts, '/capture-crop', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ capture_id: capture.id, bbox }),
      });
      if (cropRes.status === 503) {
        throw new Error('The edge is busy restarting its recogniser. Try again in a moment.');
      }
      if (!cropRes.ok) {
        const body = await cropRes.json().catch(() => null);
        throw new Error(body?.error || `The edge could not crop that face (HTTP ${cropRes.status}).`);
      }
      const { image, embedding } = await cropRes.json();
      if (!embedding) throw new Error('No face could be read from that selection.');

      const uploadRes = await api.uploadPhoto(image);
      // photo_url is the MACHINE image and is what the server re-embeds, so
      // it must be the single-face crop: face_embedding.py refuses a frame
      // holding more than one face, and this flow exists precisely because
      // two people are often in frame. display_photo_url is what the guard
      // sees in the gallery grid - the crop too, so nobody is shown a piece
      // of wall.
      await api.addAuthorizedPersonImage(activeStoreId, personId, {
        photo_url: uploadRes.url,
        display_photo_url: uploadRes.url,
        face_embedding: embedding,
      });

      showAlert('Image added to the Authorized Person.');
      setAddToAuthModal(null);
    } catch (err: any) {
      console.error('Add to Authorized error:', err);
      showAlert('Failed to add image: ' + err.message);
    } finally {
      setAddingToAuth(false);
    }
  };

  // --- Flag as POI duplicate handling ---
  const [selectedCaptureForFlag, setSelectedCaptureForFlag] = useState<any>(null);
  const [flagCaptureForm, setFlagCaptureForm] = useState({ sex: '', threatLevel: '', notes: '' });
  const [duplicatePOI, setDuplicatePOI] = useState<{ code: string; photoUrl: string; id: string } | null>(null);
  const [pendingCapture, setPendingCapture] = useState<any>(null);
  const [pendingEmbedding, setPendingEmbedding] = useState<number[] | null>(null);
  // duplicate action modal
  // v0.4.9.1.7: added `selectedPoi` to remember the user's original pick from the search list.
  // v0.4.9.1.8: each POI now has `crossNetwork` flag + `networkName` for cross-network warnings.
  const [duplicateActionModal, setDuplicateActionModal] = useState<{
    capture: any;
    embedding: number[];
    duplicates: Array<{ id: string; code: string; threatLevel?: string; photoUrl?: string; similarity?: number; crossNetwork?: boolean; networkName?: string }>;
    selectedPoi: { id: string; code: string; crossNetwork: boolean; networkName: string } | null;
  } | null>(null);
  const [addingToPoi, setAddingToPoi] = useState(false);

  // Check local network get the recent captures
  // v0.4.9.1.14: HTTPS → HTTP fallback. The previous v0.4.9.1.13 version
  // tried ONLY the hostname (HTTPS) and never fell back to IP+HTTP when
  // DNS didn't resolve, breaking both PWA and APK. We now try hostname
  // first (for APK on Android 9+), then fall back to IP+HTTP (for PWA),
  // and only mark the network unreachable when BOTH paths fail.
  const checkLocalNetwork = async () => {
    console.log('checkLocalNetwork called');
    const ip = activeStoreFull?.edge_server_ip;
    const port = activeStoreFull?.edge_server_port ?? 3002;
    const hostname = activeStoreFull?.hostname;
    console.log('Edge IP:', ip, 'Port:', port, 'Hostname:', hostname);
    if (!ip && !hostname) {
      console.warn('No edge_server_ip or hostname configured for this store');
      setIsLocalNetwork(false);
      setShowLocalCaptures(true);
      return;
    }

    // AbortController so we don't apply state after unmount or a stale call
    const controller = new AbortController();
    localNetAbortRef.current?.abort();
    localNetAbortRef.current = controller;

    const edgeOpts = { hostname, edgeIp: ip, edgePort: port };

    try {
      // v0.4.9.1.14: use the fallback helper (HTTPS via hostname → HTTP via IP).
      // The old version hardcoded only one of the two and silently broke when
      // the chosen one failed. Now both are tried before we declare offline.
      const pingRes = await edgeFetchWithFallback(edgeOpts, '/ping', { signal: controller.signal });
      console.log('Ping response:', pingRes ? pingRes.status : 'NO_RESPONSE');
      if (pingRes && pingRes.ok) {
        const capsRes = await edgeFetchWithFallback(edgeOpts, '/captures', { signal: controller.signal });
        if (capsRes && capsRes.ok) {
          const caps = await capsRes.json();
          console.log('Captures count from edge:', caps.length);
          if (!controller.signal.aborted) {
            setLocalCaptures(caps);
            setIsLocalNetwork(true);
          }
        } else if (!controller.signal.aborted) {
          setIsLocalNetwork(false);
        }
      } else if (!controller.signal.aborted) {
        setIsLocalNetwork(false);
      }
    } catch (err: any) {
      if (err?.name !== 'AbortError') {
        console.error('Network error:', err);
        if (!controller.signal.aborted) setIsLocalNetwork(false);
      }
    }

    if (!controller.signal.aborted) setShowLocalCaptures(true);
  };

  // Derived arrays
  const displayAlerts: GuardAlert[] = useMemo(() => {
    let mappedAlerts: GuardAlert[];

    if (isDemoMode) {
      mappedAlerts = demoAlerts.map(a => ({
        ...a,
        createdAt: a.timestamp.toISOString(),
        status: (a as any).status || 'active',
        poi: a.poi ? { ...a.poi, editHistory: a.poi.editHistory || [] } : null,
        capture_image_url: a.capture_image_url || null,
      })) as unknown as GuardAlert[];
    } else if (user?.role === 'cadmin') {
      mappedAlerts = clientAlerts.map(ca => ({
        id: ca.id,
        poi: ca.poi || null,
        store_id: ca.store_id,
        created_at: ca.created_at,
        status: ca.status,
        acknowledged_by: ca.acknowledged_by,
        acknowledgedByName: ca.acknowledged_by_name,
        acknowledgedBy: ca.acknowledged_by,
        event_type: ca.event_type,
        capture_image_url: ca.capture_image_url,
        createdAt: ca.created_at,
        store_name: ca.store_name,
        note: (ca as any).note || null,
      } as unknown as GuardAlert));
    } else {
      // Normal store user (guard/admin)
      mappedAlerts = alerts.map(a => ({
        ...a,
        isAcknowledgedLocally: false,
        createdAt: a.created_at,
        acknowledgedBy: a.acknowledged_by,
        acknowledgedByName: a.acknowledgedByName,
        poi: a.poi_id ? a.poi : null,
        note: a.note || null,
      })) as GuardAlert[];
    }

    // Filter out admin_alert for non-SADMIN/superadmin users
    //const isSadmin = user?.role === 'sadmin' || user?.role === 'superadmin';
    const isSadmin = (user?.role as string) === 'sadmin' || user?.role === 'superadmin';
    return mappedAlerts.filter(alert => {
      if (alert.event_type === 'admin_alert' && !isSadmin) {
        return false;
      }
      return true;
    });
  }, [isDemoMode, demoAlerts, alerts, clientAlerts, user?.role]);

    const unacknowledgedAlerts = useMemo(() => displayAlerts.filter(a => !a.acknowledgedBy && a.status !== 'completed'), [displayAlerts]);
    const acknowledgedAlerts = useMemo(() => displayAlerts.filter(a => a.acknowledgedBy && a.status !== 'completed'), [displayAlerts]);
    const hasAlerts = displayAlerts.length > 0;

    // Carousel active alerts selection
    const activeCarouselAlerts = useMemo(() => {
      if (unacknowledgedAlerts.length > 0) return unacknowledgedAlerts;
      if (acknowledgedAlerts.length > 0 && showAcknowledgedStack) return acknowledgedAlerts;
      return [];
    }, [unacknowledgedAlerts, acknowledgedAlerts, showAcknowledgedStack]);

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, align: 'center' });
  const carouselMountedRef = useRef(false);
  // Per-alert mutex for acknowledge/complete so out-of-order responses don't trample each other
  const mutatingAlertsRef = useRef<Set<string>>(new Set());
  const localNetAbortRef = useRef<AbortController | null>(null);
  const prevAlertsRef = useRef<GuardAlert[]>([]); 

  const { playAlert, stopAlert, isSoundEnabled, toggleSound } = useAlertSound();
  const intervalRef = useRef<number | null>(null);

  //Fetch the full store when activeStoreId changes
  useEffect(() => {
    if (!activeStoreId) {
      setActiveStoreFull(null);
      return;
    }
    api.getStores().then(stores => {
      const found = stores.find(s => s.id === activeStoreId);
      console.log('Fetched full store:', found);
      setActiveStoreFull(found || null);
    }).catch(err => console.error('Failed to fetch store details:', err));
  }, [activeStoreId]);

  const edgeIp = activeStoreFull?.edge_server_ip;
  const edgePort = activeStoreFull?.edge_server_port ?? 3002;
  // v0.4.9.1.13: prefer Edge hostname (HTTPS) over IP (HTTP) for image URLs.
  // Cert SAN covers *.isentinel360 so https://${hostname} works on Android 9+.
  const edgeHostname = activeStoreFull?.hostname;

  // --- Live feed (v0.4.9.4.7) ---
  // GDPR: the feed is only ever shown when the browser can reach the edge on
  // the LOCAL network (isLocalNetwork, proved by a successful /ping to the
  // edge's LAN IP:port). Off-site that check fails, the panel is not
  // reachable and nothing is requested, so a guard cannot pull store video
  // from home. Do not add a fallback that fetches /cameras or /live/* from the
  // VPS — that would put the footage on the public internet.
  const [liveView, setLiveView] = useState<'off' | 'list' | 'feed'>('off');
  const [liveCameras, setLiveCameras] = useState<any[]>([]);
  const [liveCamera, setLiveCamera] = useState<any>(null);
  const [liveRung, setLiveRung] = useState(0);
  const [liveTick, setLiveTick] = useState(0);
  const [liveLoading, setLiveLoading] = useState(false);
  const [liveError, setLiveError] = useState<string | null>(null);
  const [netProbeDone, setNetProbeDone] = useState(false);
  const liveImgRef = useRef<HTMLImageElement | null>(null);
  const liveTimerRef = useRef<any>(null);

  // The live feed must go to the edge over the SAME ladder everything else
  // uses: hostname -> https://<ip> -> http://<ip>:<port>. On store WiFi the
  // hostname does not resolve and the bare-IP https rung has no certificate
  // that covers it, so only the third rung works. Listing the rungs once and
  // indexing into them (rather than using .primary) is what makes the feed
  // work at all on the network where it is actually used.
  const liveRungs = useMemo(() => {
    const s = buildEdgeImageSrc({ hostname: edgeHostname, edgeIp, edgePort }, '/live');
    return [s.primary, s.ipHttps, s.fallback].filter(Boolean) as string[];
  }, [edgeHostname, edgeIp, edgePort]);

  // Lightweight reachability probe on mount. Deliberately NOT
  // checkLocalNetwork(): that also fetches every capture and flips
  // showLocalCaptures, which would drag the guard into Recents at login.
  // This only answers "is the edge reachable from here?" — which is all the
  // Live button needs to decide whether to offer itself.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await edgeFetchWithFallback({ hostname: edgeHostname, edgeIp, edgePort }, '/ping');
        if (!cancelled) setIsLocalNetwork(!!(res && res.ok));
      } catch {
        if (!cancelled) setIsLocalNetwork(false);
      } finally {
        if (!cancelled) setNetProbeDone(true);
      }
    })();
    return () => { cancelled = true; };
  }, [edgeHostname, edgeIp, edgePort]);

  const stopLivePolling = useCallback(() => {
    if (liveTimerRef.current) {
      clearTimeout(liveTimerRef.current);
      liveTimerRef.current = null;
    }
  }, []);

  const exitLive = useCallback(() => {
    stopLivePolling();
    setLiveView('off');
    setLiveCamera(null);
    setLiveCameras([]);
    setLiveError(null);
  }, [stopLivePolling]);

  // React owns `src` outright. An earlier version set img.src imperatively from
  // the poll timer AND declared src={liveRungs[0]} in JSX — on every re-render
  // React stamped the hostname back over the rung the ladder had advanced to,
  // so the feed burned through all three rungs in two seconds and then declared
  // the camera unreachable. One writer, one source of truth: a tick in state.
  const pollLiveFrame = useCallback(() => {
    if (liveView !== 'feed' || !liveCamera) return;
    setLiveTick(Date.now());
    liveTimerRef.current = setTimeout(pollLiveFrame, 1000);
  }, [liveView, liveCamera]);

  const liveSrc = useMemo(() => {
    if (!liveCamera || !liveRungs.length) return '';
    const base = liveRungs[Math.min(liveRung, liveRungs.length - 1)];
    // The cache buster is what keeps this a feed: without it the browser
    // reuses the first frame forever and the picture looks frozen.
    return base ? `${base}/${liveCamera.index}?t=${liveTick}` : '';
  }, [liveCamera, liveRung, liveRungs, liveTick]);

  // One rung down per failure, and once the last rung has failed we stop and
  // say so. Guarding against a runaway retry matters: on an HTTPS page the
  // http:// rung is blocked as mixed content and fails instantly, so an
  // unguarded retry would hammer the edge every second.
  const onLiveFrameError = useCallback(() => {
    if (liveRung < liveRungs.length - 1) {
      setLiveRung(liveRung + 1);
    } else {
      stopLivePolling();
      setLiveError(t('guard.cameraUnreachable'));
    }
  }, [liveRung, liveRungs.length, stopLivePolling, t]);

  const retryLive = useCallback(() => {
    setLiveError(null);
    setLiveRung(0);
  }, []);

  useEffect(() => {
    if (liveView === 'feed') pollLiveFrame();
    else stopLivePolling();
    return stopLivePolling;
  }, [liveView, liveCamera, pollLiveFrame, stopLivePolling]);

  const openLiveList = useCallback(async () => {
    setLiveView('list');
    setLiveLoading(true);
    setLiveError(null);
    try {
      const opts = { hostname: edgeHostname, edgeIp, edgePort };
      const res = await edgeFetchWithFallback(opts, '/cameras');
      if (!res || !res.ok) throw new Error('The edge did not answer the camera list.');
      const data = await res.json();
      setLiveCameras(Array.isArray(data?.cameras) ? data.cameras : []);
      if (!data?.cameras?.length) setLiveError('This edge reports no cameras.');
    } catch (e: any) {
      setLiveCameras([]);
      setLiveError(e?.message || 'Could not reach the edge.');
    } finally {
      setLiveLoading(false);
    }
  }, [edgeHostname, edgeIp, edgePort]);

  // Update the <meta name="theme-color"> based on alert state
  useEffect(() => {
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
      meta.setAttribute('content', hasAlerts ? '#dc2626' : '#16a34a'); // red or green
    }
  }, [hasAlerts]);

  // Automatically switch to LIVE mode for non-demo users
  useEffect(() => {
    if (user && user.email !== 'demoadmin@isentinel360.com' && user.email !== 'demoguard@isentinel360.com') {
      useDemoStore.setState({ isDemoMode: false });
    }
  }, [user]);

  // cadmin/superadmin never see the store‑selector – auto‑pick the first store
  // modified, commented out since now we want the cadmin and sadmin to get the selector
  /*
  useEffect(() => {
    if (
      user &&
      user.stores &&
      user.stores.length > 1 &&
      !activeStoreId &&
      user.role === 'cadmin'
    ) {
      setActiveStoreId(user.stores[0].store_id);
    }
  }, [user, activeStoreId, setActiveStoreId]);
  */

  // Fetch real alerts periodically if not demo mode
  useEffect(() => {
    if (isDemoMode) {
      setLoading(false);
      return;
    }

    let timer: ReturnType<typeof setTimeout> | null = null;

    const fetchAlerts = async () => {
      if (mutatingAlertsRef.current.size > 0) {
        timer = setTimeout(fetchAlerts, 500);
        return;
      }
      try {
        let data;
        // v0.4.9.2.4c5: TAdmin added to the "all alerts" branch.
        //
        // A TAdmin has no store_users membership, so the /api/alerts/active
        // path (the else branch) 403s for them — it is store-scoped and they
        // are global by design. They need the full feed to verify a camera
        // install: watch an alert fire, confirm the capture image, then ack.
        if (user?.role === 'superadmin' || user?.role === 'sadmin' || user?.role === 'tadmin') {
          data = await api.getAllAlerts();
        } else {
          data = await api.getActiveAlerts();
        }

        if (mutatingAlertsRef.current.size === 0) {
          const mapped = data.map((a: any) => ({
            ...a,
            isAcknowledgedLocally: false,
            createdAt: a.created_at,
            acknowledgedBy: a.acknowledged_by,
            acknowledgedByName: a.acknowledgedByName,
            //poi: a.poi_id ? a.poi : null,
            poi: a.poi_id ? {
              ...a.poi,
              isMostWanted: a.source_type === 'most_wanted' || a.poi?.source_type === 'most_wanted' || false,
              editHistory: a.poi?.editHistory || [],
            } : null,
            isMostWanted: a.source_type === 'most_wanted' || a.poi?.source_type === 'most_wanted' || false,
            similarity: a.similarity || null, 
            store_name: a.store_name || null,
            note: a.note || null,
          })) as GuardAlert[];
          setAlerts(mapped);
          setError(null);
        }
      } catch (err: any) {
        console.error('Failed to fetch alerts:', err);
        setError(err.message);
      } finally {
        setLoading(false);
        timer = setTimeout(fetchAlerts, 5000);
      }
    };

    fetchAlerts();

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [isDemoMode, user?.role]);

  // Fetch client-wide alerts for cadmin
  useEffect(() => {
    if (user?.role === 'cadmin' && !isDemoMode) {
      const fetchClientAlerts = async () => {
        try {
          const data = await api.getClientAlerts();
          setClientAlerts(data);
        } catch (err) { console.error(err); }
      };
      fetchClientAlerts();
      const interval = setInterval(fetchClientAlerts, 5000);
      return () => clearInterval(interval);
    }
  }, [user?.role, isDemoMode]);

  // fetch client alerts when role is cadmin (client chied of security)
  useEffect(() => {
      if (user?.role === 'cadmin' && !isDemoMode) {
        const fetchClientAlerts = async () => {
          try {
            const data = await api.getClientAlerts();
            setClientAlerts(data);
          } catch (err) { console.error(err); }
        };
        fetchClientAlerts();
        const interval = setInterval(fetchClientAlerts, 5000);
        return () => clearInterval(interval);
      }
    }, [user?.role, isDemoMode]);

  // Force view to alerts when there are active alerts
  useEffect(() => {
    if (hasAlerts) {
      setViewMode('alerts');
    }
  }, [hasAlerts]);

  // Sound control: only loop if there is at least one unacknowledged alert
  useEffect(() => {
    const unacknowledgedCount = displayAlerts.filter(a => !a.acknowledgedBy && a.status !== 'completed').length;
    const shouldLoop = unacknowledgedCount > 0 && isSoundEnabled;
    if (shouldLoop) {
      playAlert();
      intervalRef.current = window.setInterval(() => playAlert(), 2000);
    } else {
      stopAlert();
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      stopAlert();
    };
  }, [displayAlerts, isSoundEnabled, playAlert, stopAlert]);

  // Carousel effects
  useLayoutEffect(() => {
    if (!emblaApi) return;

    const slidesChanged =
      prevAlertsRef.current.length !== activeCarouselAlerts.length ||
      prevAlertsRef.current.some((a, i) => a.id !== activeCarouselAlerts[i]?.id);

    if (activeCarouselAlerts.length === 0) {
      carouselMountedRef.current = false;
      prevAlertsRef.current = activeCarouselAlerts;
      return;
    }

    carouselMountedRef.current = true;
    prevAlertsRef.current = activeCarouselAlerts;

    if (slidesChanged) {
      const id = requestAnimationFrame(() => {
        if (!emblaApi) return;
        emblaApi.reInit();
        const safeIndex = Math.min(selectedIndex, activeCarouselAlerts.length - 1);
        emblaApi.scrollTo(Math.max(0, safeIndex), true);
      });
      return () => cancelAnimationFrame(id);
    } else {
      emblaApi.scrollTo(selectedIndex);
    }
  }, [activeCarouselAlerts, emblaApi]);  // ← selectedIndex intentionally removed

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    const subscriber: any = emblaApi.on('select', onSelect);
    return () => {
      if (typeof subscriber === 'function') subscriber();
      else if (subscriber && typeof subscriber.remove === 'function') subscriber.remove();
    };
  }, [emblaApi]);

  useEffect(() => {
    if (emblaApi) {
      setCarouselError(false);
    } else if (activeCarouselAlerts.length > 0) {
      const timer = setTimeout(() => {
        if (!emblaApi) setCarouselError(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [emblaApi, activeCarouselAlerts.length]);

  // iSentiAlert (D16): local notification fallback. Re-added because
  // web-push is unreliable (the original reason iSentiAlert exists),
  // and desktop guards (security-office scenario) need a reliable
  // notification path even when the PWA is open. This local path is
  // gated by the SAME toggle as the web-push path (via
  // effectiveWebPushEnabled from src/lib/browserNotifications.ts), so
  // the user has a single source of truth for "should browser
  // notifications fire?".
  //
  // Yes, this can produce a duplicate notification when web-push ALSO
  // fires (mobile case). That's an accepted trade-off for v1: web-push
  // is best-effort, the local path is the reliable lifeline for
  // desktop. On the iSentiAlert-equipped mobile, the FCM path takes
  // over and the PWA notifications become redundant (handled in
  // Phase 3).
  //
  // Deduplication is via localStorage (lastNotifiedAlertIds): we
  // persist the IDs of all alerts we've ever notified about. On a
  // fresh visit (empty set), we seed the set with the CURRENT alerts
  // (no flood) and don't fire. On subsequent polls, only newly-arrived
  // alert IDs trigger a notification.
  //
  // Note: the previous version of this code was guarded by an
  // isMobile check. That was a leftover from when this was the
  // mobile-only fallback. The new design runs on ALL platforms
  // (desktop included) because desktop is where the unreliability
  // matters most — guards in security offices rely on the PWA on
  // a desktop PC.
  useEffect(() => {
    if (isDemoMode || !user) return;
    if (typeof window === 'undefined' || typeof window.Notification === 'undefined') return;
    if (!effectiveWebPushEnabled()) return; // Settings toggle is OFF
    if (window.Notification.permission !== 'granted') return;

    let notifiedIds: Set<string>;
    try {
      notifiedIds = readNotifiedAlertIds();
      if (notifiedIds.size === 0) {
        // First visit: seed with current alerts, do not fire.
        // This prevents a flood of notifications on the user's
        // first login (the alerts are already-known, not "new").
        const seeded = displayAlerts.map(a => a.id);
        writeNotifiedAlertIds(seeded);
        return;
      }
    } catch {
      return;
    }

    const newAlerts = displayAlerts.filter(a => !notifiedIds.has(a.id));
    if (newAlerts.length === 0) return;

    newAlerts.forEach(alert => {
      try {
        // v0.4.9.2.4c5: `vibrate` is valid in the SERVICE-WORKER
        // NotificationOptions spec but absent from lib.dom's `Notification`,
        // so TypeScript rejected it (TS2353). More importantly it is
        // simply ignored by the window Notification constructor, so it
        // bought nothing here. Removed rather than cast — the try/catch
        // around this call was swallowing the resulting error, which
        // meant these alerts could silently fail to appear.
        new window.Notification('Security Alert', {
          body: alert.poi ? `Match detected: ${alert.poi.code}` : 'Unauthorized access',
          icon: '/logo-192x192.png',
          requireInteraction: true,
        });
      } catch {
        // one bad alert must NEVER crash the whole tree — swallow silently
      }
    });

    // Persist the updated set (capped at 200 inside writeNotifiedAlertIds)
    const updated = new Set<string>([...notifiedIds, ...newAlerts.map(a => a.id)]);
    writeNotifiedAlertIds(updated);
  }, [displayAlerts, isDemoMode, user]);

  const handlePrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const handleNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  // Alert actions
  const handleAcknowledge = async (alertId: string) => {
    // Stop alert sound immediately
    stopAlert();
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (!isDemoMode) {
      if (user?.role === 'cadmin') {
        // Optimistic update on clientAlerts
        const original = clientAlerts.find(a => a.id === alertId);
        setClientAlerts(prev =>
          prev.map(a =>
            a.id === alertId
              ? { ...a, status: 'acknowledged', acknowledged_by: String(user.id), acknowledged_by_name: user.name }
              : a
          )
        );
        try {
          await api.acknowledgeAlert(alertId);
        } catch (err:any) {
          console.error(err);
          //window.alert(t('guard.acknowledgeError'));
          showAlert(err.message || t('guard.acknowledgeError'));
          // Revert
          if (original) {
            setClientAlerts(prev =>
              prev.map(a => (a.id === alertId ? original : a))
            );
          }
        }
      } else {
        // Guard / Admin
        if (mutatingAlertsRef.current.has(alertId)) return; // already in flight for this alert
        const originalAlert = alerts.find(a => a.id === alertId);
        mutatingAlertsRef.current.add(alertId); // per-alert lock
        try {
          flushSync(() => {
            setAlerts(prev => prev.map(a =>
              a.id === alertId ? {
                ...a,
                status: 'acknowledged',
                acknowledgedBy: user?.name ?? (user?.id != null ? String(user.id) : undefined),
                acknowledgedByName: user?.name,
              } : a
            ));
          });
          await api.acknowledgeAlert(alertId);
        } catch (err:any) {
          console.error(err);
          //window.alert(t('guard.acknowledgeError'));
          showAlert(err.message || t('guard.acknowledgeError'));
          if (originalAlert) {
            setAlerts(prev => prev.map(a => (a.id === alertId ? originalAlert : a)));
          }
        } finally {
          mutatingAlertsRef.current.delete(alertId);
        }
      }
    } else {
      // Demo mode
      const guardName = user?.name || 'Guard';
      demoAcknowledge(alertId, guardName);
    }
  };

  const handleComplete = async (alertId: string, report: string) => {
        if (!isDemoMode) {
          if (user?.role === 'cadmin') {
            const original = clientAlerts.find(a => a.id === alertId);
            setClientAlerts(prev => prev.filter(a => a.id !== alertId));
            try {
              await api.completeAlert(alertId, report);
            } catch (err:any) {
              console.error(err);
              //window.alert(t('guard.completeError'));
              showAlert(err.message || t('guard.completeError'));
              if (original) {
                setClientAlerts(prev => [...prev, original]);
              }
            }
          } else {
            const originalAlert = alerts.find(a => a.id === alertId);
            if (mutatingAlertsRef.current.has(alertId)) return;
            mutatingAlertsRef.current.add(alertId);
            setAlerts(prev => prev.filter(a => a.id !== alertId));
            try {
              await api.completeAlert(alertId, report);
            } catch (err:any) {
              console.error(err);
              //window.alert(t('guard.completeError'));
              showAlert(err.message || t('guard.completeError'));
                if (originalAlert) setAlerts(prev => [...prev, originalAlert]);
              } finally {
                mutatingAlertsRef.current.delete(alertId);
              }
            }
          } else {
            useDemoStore.getState().dismissAlert(alertId);
          }
        };

  const handleAccompliceClick = (acc: AccompliceFromAPI, mainPoi: any) => {
    setSelectedPoiForModal({
      id: acc.id,
      code: acc.code,
      threatLevel: acc.threatLevel,
      photoUrl: acc.photoUrl,
      notes: acc.notes || '',
      lastSeen: mainPoi?.lastSeen,
    } as POIFromAPI);
  };

  const handleDemoSimulate = (poi: any) => {
    demoTriggerAlert(poi);
    playAlert();
  };

  // Face flagging (demo only)
  const [selectedFace, setSelectedFace] = useState<any>(null);
  const [flagFormData, setFlagFormData] = useState({ sex: '', threatLevel: '', notes: '' });
  const handleFlagSubmit = async (faceId: string) => {
    if (!flagFormData.sex || !flagFormData.threatLevel) return;

    if (isDemoMode) {
      const newCode = `ES-MAD-${Math.floor(Math.random() * 90000) + 10000}-${flagFormData.sex}-${
        flagFormData.threatLevel === 'Low' ? '1' : flagFormData.threatLevel === 'Medium' ? '2' : '3'
      }`;
      demoFlagFace(faceId, {
        threatLevel: flagFormData.threatLevel as any,
        notes: flagFormData.notes,
        code: newCode,
        sex: flagFormData.sex as any,
      }, user?.name || 'Guard');
    } else {
      try {
        const face = demoRecentFaces.find(f => f.id === faceId);
        if (!face) return;

        // Generate a proper code
        const store = user?.stores?.[0]; // or use activeStoreId
        const country = 'ES'; // should come from store settings, but we'll use a fallback
        const city = 'MAD';
        const random = Math.floor(10000 + Math.random() * 90000);
        const threatDigit = flagFormData.threatLevel === 'Low' ? '1' : flagFormData.threatLevel === 'Medium' ? '2' : '3';
        const code = `${country}-${city}-${random}-${flagFormData.sex}-${threatDigit}`;

        await api.createPOI({
          code,
          threatLevel: flagFormData.threatLevel as any,
          photoUrl: face.imageUrl,
          notes: flagFormData.notes,
          storeId: activeStoreId || user?.stores?.[0]?.store_id,
        });
        alert('POI created successfully');
      } catch (err: any) {
        alert('Failed to create POI: ' + err.message);
      }
    }
    setSelectedFace(null);
    setFlagFormData({ sex: '', threatLevel: '', notes: '' });
  };

  // --- Add to POI functions ---
  const handleAddToPoiClick = async (capture: any) => {
    try {
      // v0.4.9.1.14: HTTPS (hostname) → HTTP (IP) fallback so the PWA still
      // works on local WiFi even when the Edge hostname doesn't resolve.
      const edgeOpts = { hostname: edgeHostname, edgeIp, edgePort };
      const detailsRes = await edgeFetchWithFallback(edgeOpts, `/captures/${capture.id}/details`);
      if (!detailsRes || !detailsRes.ok) throw new Error('Failed to fetch capture details');
      const details = await detailsRes.json();
      if (!details.embedding) throw new Error('No embedding found for this capture');

      const pois = await api.getPOIs();
      setAddToPoiModal({
        capture,
        embedding: details.embedding,
        photoUrl: buildEdgeImageSrc(edgeOpts, `/captures/${capture.id}`).primary, // Edge URL
      });
      setPoiSearchResults(pois);
      setPoiSearchQuery('');
    } catch (err: any) {
      showAlert('Failed to load POIs: ' + err.message);
    }
  };

  const handleAddToPoiConfirm = async (poiId: string, options?: { skipGlobalCheck?: boolean }) => {
    if (!addToPoiModal) return;
    const { capture, embedding } = addToPoiModal;
    setAddingToPoi(true);
    try {
      // --- GLOBAL DUPLICATE CHECK (unless skipped) ---
      if (!options?.skipGlobalCheck) {
        const duplicateResult = await api.post('/api/duplicates/check-embedding', { embedding });
        if (duplicateResult.duplicate && duplicateResult.existing) {
          // v0.4.9.1.8: detect cross-network duplicate. The matched POI (Y) might
          // belong to a different network. The user can't add to it across networks
          // — surface as a warning + disable the Y option. The user's selection (X)
          // still works (it belongs to their store).
          let crossNetwork = false;
          let networkName = '';
          try {
            const store = await api.getStore(duplicateResult.existing.store_id || '');
            const myStoreId = localStorage.getItem('activeStoreId');
            if (store && store.id && store.client_id && myStoreId) {
              const myStore = await api.getStore(myStoreId);
              if (myStore && store.client_id !== myStore.client_id) {
                crossNetwork = true;
                networkName = store.client_name || 'another network';
              }
            }
          } catch (e) {
            // best-effort - don't block the modal if store lookup fails
            console.warn('[iSentinel360] Cross-network check failed:', e);
          }

          // Build POI-like object for the duplicate modal
          const existingPoi = {
            id: duplicateResult.existing.id,
            code: duplicateResult.existing.code,
            threatLevel: duplicateResult.existing.threat_level || 'Unknown',
            photoUrl: duplicateResult.existing.photo_url || '',
            similarity: duplicateResult.existing.similarity,
            crossNetwork,
            networkName,
          };

          // v0.4.9.1.7: include the user's selected POI in the modal state
          // so they can choose to add to their selection (X) or the matched duplicate (Y).
          const selectedPoi = poiSearchResults.find(p => p.id === poiId);
          const selectedPoiModal = selectedPoi ? {
            id: selectedPoi.id,
            code: selectedPoi.code,
            crossNetwork: false,  // user's selection is always in their network
            networkName: '',
          } : null;

          setDuplicateActionModal({
            capture,
            embedding,
            duplicates: [existingPoi],
            selectedPoi: selectedPoiModal,
          });
          setAddingToPoi(false);
          return;
        }
      }

      // --- INTERNAL DUPLICATE CHECK (within the selected POI's gallery) ---
      const { images } = await api.getPoiImages(poiId);
      const THRESHOLD = 0.65;
      let isInternalDuplicate = false;
      for (const img of images) {
        if (!img.faceEmbedding) continue;
        if (img.faceEmbedding.length !== embedding.length) continue;
        const sim = cosineSimilarity(embedding, img.faceEmbedding);
        if (sim >= THRESHOLD) {
          isInternalDuplicate = true;
          break;
        }
      }
      if (isInternalDuplicate) {
        showAlert('This face is already present in the POI gallery. Duplicate images are not allowed.');
        setAddingToPoi(false);
        setAddToPoiModal(null);
        return;
      }

      // --- Upload & Add Image ---
      // v0.4.9.1.14: HTTPS (hostname) → HTTP (IP) fallback so PWA still works.
      const edgeOpts = { hostname: edgeHostname, edgeIp, edgePort };
      const imgRes = await edgeFetchWithFallback(edgeOpts, `/captures/${capture.id}`);
      if (!imgRes || !imgRes.ok) throw new Error('Failed to fetch capture image');
      const blob = await imgRes.blob();
      const dataUrl = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.readAsDataURL(blob);
      });
      const uploadRes = await api.uploadPhoto(dataUrl);
      const photoUrl = uploadRes.url;

      const token = localStorage.getItem('authToken');
      let userId = '';
      try { userId = JSON.parse(atob(token?.split('.')[1] || '')).userId || ''; } catch {}

      await api.addPoiImage(poiId, {
        photo_url: photoUrl,
        face_embedding: embedding,
        created_by: userId,
        justification: `Added from Guard Dashboard by ${user?.name || 'Guard'}`,
      });

      showAlert(t('admin.imageAdded') || 'Image added to POI successfully!');
      setAddToPoiModal(null);
      setDuplicateActionModal(null);
    } catch (err: any) {
      console.error('Add to POI error:', err);
      showAlert('Failed to add image to POI: ' + err.message);
    } finally {
      setAddingToPoi(false);
    }
  };

  // --- Flag as POI duplicate handling ---
  const handleFlagCaptureSubmit = async (capture: any) => {
    if (!flagCaptureForm.sex || !flagCaptureForm.threatLevel) {
      showAlert(t('flag.missingFields') || 'Please select sex and threat level');
      return;
    }
    try {
      // v0.4.9.1.14: HTTPS (hostname) → HTTP (IP) fallback so PWA still works.
      const edgeOpts = { hostname: edgeHostname, edgeIp, edgePort };
      const detailsRes = await edgeFetchWithFallback(edgeOpts, `/captures/${capture.id}/details`);
      if (!detailsRes || !detailsRes.ok) throw new Error('Failed to fetch capture details');
      const details = await detailsRes.json();
      if (!details.embedding) throw new Error('No embedding found for this capture');

      // Use check-embedding endpoint for better duplicate detection
      const duplicateResult = await api.post('/api/duplicates/check-embedding', { embedding: details.embedding });
      if (duplicateResult.duplicate && duplicateResult.existing) {
        // Build POI-like object from response
        const existingPoi = {
          id: duplicateResult.existing.id,
          code: duplicateResult.existing.code,
          threatLevel: duplicateResult.existing.threat_level || 'Unknown',
          photoUrl: duplicateResult.existing.photo_url || '',
          similarity: duplicateResult.existing.similarity,
        };
        // v0.4.9.1.13: handleFlagCaptureSubmit doesn't have a selected POI yet (creating new).
        // selectedPoi is null because this flow is "flag capture as duplicate of existing POI"
        // — the user will choose what to do from the DuplicateActionModal.
        setDuplicateActionModal({
          capture,
          embedding: details.embedding,
          duplicates: [{ ...existingPoi, crossNetwork: false, networkName: '' }],
          selectedPoi: null,
        });
        return;
      }

      // No duplicate found – proceed to create
      await createPOIFromCapture(capture, details.embedding);
    } catch (err: any) {
      showAlert('Failed to create POI: ' + err.message);
    }
  };

  const createPOIFromCapture = async (capture: any, embedding: number[]) => {
    try {
      // v0.4.9.1.14: HTTPS (hostname) → HTTP (IP) fallback so PWA still works.
      const edgeOpts = { hostname: edgeHostname, edgeIp, edgePort };
      const imgRes = await edgeFetchWithFallback(edgeOpts, `/captures/${capture.id}`);
      if (!imgRes || !imgRes.ok) throw new Error('Failed to fetch capture image');
      const blob = await imgRes.blob();
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = async () => {
        try {
          const base64data = reader.result as string;
          const uploadRes = await api.uploadPhoto(base64data);
          const photoUrl = uploadRes.url;

          const country = activeStoreFull?.country_code || 'ES';
          const city = activeStoreFull?.city_code || 'MAD';
          const random = Math.floor(10000 + Math.random() * 90000);
          const threatDigit = flagCaptureForm.threatLevel === 'Low' ? '1' : flagCaptureForm.threatLevel === 'Medium' ? '2' : '3';
          const code = `${country}-${city}-${random}-${flagCaptureForm.sex}-${threatDigit}`;

          await api.createPOI({
            code,
            threatLevel: flagCaptureForm.threatLevel as any,
            photoUrl,
            notes: flagCaptureForm.notes,
            storeId: activeStoreId || user?.stores?.[0]?.store_id,
            faceEmbedding: embedding,
          });

          showAlert(t('flag.poiCreated') || 'POI created successfully');
          setSelectedCaptureForFlag(null);
          setFlagCaptureForm({ sex: '', threatLevel: '', notes: '' });
          setDuplicateActionModal(null);
        } catch (innerErr: any) {
          console.error('createPOIFromCapture inner error:', innerErr);
          showAlert('Failed to create POI: ' + (innerErr?.message || 'Unknown error'));
        }
      };
      reader.onerror = () => {
        showAlert('Failed to read capture image');
      };
    } catch (err: any) {
      showAlert('Failed to create POI: ' + err.message);
    }
  };

  const handleAlertsButton = () => {
    // If already in alerts view, and only acknowledged alerts exist, toggle the acknowledged stack
    if (viewMode === 'alerts') {
      if (unacknowledgedAlerts.length === 0 && acknowledgedAlerts.length > 0) {
        setShowAcknowledgedStack(prev => !prev);
        return;
      }
    }
    // Otherwise, just show alerts view (default)
    setViewMode('alerts');
    setShowAcknowledgedStack(false);
  };

  // Store selector check – after all hooks, before the final return
  if (user && user.stores && user.stores.length > 1 && !activeStoreId) {
    return <StoreSelectorModal />;
  }

 return (
  <div className={cn(
    // h-dvh (dynamic viewport height) instead of h-screen (100vh).
    // 100vh is the static viewport height and includes the area
    // BEHIND the mobile browser's address bar, which makes the page
    // taller than the visible screen and allows the user to scroll
    // up/down past the address bar. h-dvh tracks the address bar as
    // it shows/hides, so the layout always fits the visible viewport.
    // Fixes an intermittent issue where the page would sometimes
    // render taller than the screen on PWA reload.
    "h-dvh flex flex-col overflow-hidden transition-colors duration-500",
    hasAlerts && viewMode === 'alerts' ? "bg-red-50 dark:bg-red-950/20" : "bg-green-50 dark:bg-green-950/20",
    // v0.4.9.4.8 — opt in to the art background. Applying it to THIS div
    // rather than <body> keeps it opt-in per screen: drop this one entry and
    // GuardDashboard goes back to the flat background, and no other screen is
    // affected. ArtAlign (mounted once in main.tsx) centres the artwork's ring
    // on the element marked [data-art-center] below.
    "art-bg"
  )}>
    <div className="flex-shrink-0">
      <Header view="guard" isSoundEnabled={isSoundEnabled} onToggleSound={toggleSound} alertActive={hasAlerts} />
    </div>

    {/* View Toggle Buttons */}
    {/* justify-center, not justify-between: there is only ONE child here, so
        justify-between pinned the button group hard left. Long labels (Russian,
        German) then made the row look ragged. flex-wrap on the inner row means
        a narrow phone folds the buttons onto a second line instead of pushing
        them off the edge where they cannot be tapped. */}
    <div className="container max-w-screen-md mx-auto px-4 pt-4 flex justify-center items-center flex-shrink-0">
      <div className="flex flex-wrap justify-center gap-2">
        <Button
          size="sm"
          onClick={handleAlertsButton}
          className={cn(
            hasAlerts
              ? "bg-red-600 hover:bg-red-700 text-white border-red-800"
              : viewMode === 'alerts'
              ? ""
              : "border-input bg-background hover:bg-accent hover:text-accent-foreground"
          )}
          variant={hasAlerts ? undefined : viewMode === 'alerts' ? 'default' : 'outline'}
        >
          <Shield className="w-4 h-4 mr-1" />
          {t('guard.alerts')}
          {hasAlerts && ` (${displayAlerts.length})`}
        </Button>

        <Button
          variant={liveView !== 'off' ? 'default' : 'outline'}
          size="sm"
          onClick={() => { if (liveView === 'off') openLiveList(); }}
          // Only grey out once we actually KNOW the edge is unreachable. Before the
          // probe finishes the answer is "unknown", and a permanently dead
          // button at login was exactly the bug this replaces.
          disabled={netProbeDone && !isLocalNetwork}
          title={netProbeDone && !isLocalNetwork ? 'Available only on the store network' : undefined}
          className={cn(hasAlerts && "bg-red-700 hover:bg-red-800 text-white border-red-800")}
        >
          <Video className="w-4 h-4 mr-1" /> {t('guard.liveFeed') || 'Live'}
        </Button>

        <Button
          variant={viewMode === 'faces' ? 'default' : 'outline'}
          size="sm"
          onClick={() => {
            exitLive();
            setViewMode('faces');
            checkLocalNetwork();
          }}
          className={cn(hasAlerts && "bg-red-700 hover:bg-red-800 text-white border-red-800")}
        >
          <Camera className="w-4 h-4 mr-1" /> {t('guard.recentFaces')}
        </Button>
      </div>
    </div>

    <main className="flex-1 min-h-0 container max-w-screen-md mx-auto px-4 py-6 flex flex-col overflow-y-auto">
      {liveView !== 'off' ? (
        // ---- LIVE FEED (v0.4.9.4.7) ----
        <div className="flex-1 flex flex-col">
          <div className="flex items-center gap-2 mb-3">
            <Button variant="outline" size="sm" onClick={exitLive}>
              <ArrowLeft className="w-4 h-4 mr-1" /> {t('guard.exit')}
            </Button>
            <h2 className="text-lg font-semibold">
              {liveView === 'feed' && liveCamera ? liveCamera.name : t('guard.liveCameras')}
            </h2>
          </div>

          {liveView === 'list' && (
            liveLoading ? (
              <p className="text-sm text-muted-foreground">{t('guard.loadingCameras')}</p>
            ) : liveError ? (
              <p className="text-sm text-destructive">{liveError}</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {liveCameras.map((c) => (
                  <button
                    key={c.index}
                    onClick={() => { setLiveCamera(c); setLiveView('feed'); setLiveRung(0); setLiveError(null); }}
                    className="text-left p-3 rounded border bg-card hover:bg-accent"
                  >
                    <p className="font-medium">{c.name}</p>
                    <p className="text-xs text-muted-foreground">{c.location || t('guard.noLocationSet')}</p>
                  </button>
                ))}
              </div>
            )
          )}

          {liveView === 'feed' && liveCamera && (
            <div className="flex-1 flex flex-col">
              <p className="text-xs text-muted-foreground mb-2">{liveCamera.location}</p>
              {liveError ? (
                <div className="p-3 rounded border border-destructive/40">
                  <p className="text-sm text-destructive">{liveError}</p>
                  <Button variant="outline" size="sm" className="mt-2" onClick={retryLive}>
                    {t('guard.tryAgain')}
                  </Button>
                </div>
              ) : (
                // Snapshot polling, not video. The edge writes one JPEG per
                // camera on its own timer and serves it no-store; we
                // re-request it every second with a cache buster, walking
                // down the hostname -> https IP -> http IP:port ladder.
                <img
                  ref={liveImgRef}
                  src={liveSrc}
                  alt={t('guard.liveViewOf', { name: liveCamera.name })}
                  className="w-full rounded bg-black"
                  onError={onLiveFrameError}
                />
              )}
              <p className="text-[10px] text-muted-foreground mt-2">
                {t('guard.liveFeedNote')}
              </p>
            </div>
          )}
        </div>
      ) : !isOnline ? <OfflineShield /> : (viewMode === 'alerts' ? (
        // ---- ALERT VIEW (unchanged) ----
        !hasAlerts ? (
          // Idle green shield
          <div className="flex-1 flex flex-col items-center justify-center">
            <div className="relative w-48 h-48" data-art-center>
              <div className="absolute inset-0 bg-green-500/20 rounded-full animate-ping" />
              <div className="absolute inset-0 bg-green-500/10 rounded-full animate-pulse" />
              <Shield className="w-48 h-48 text-green-600 dark:text-green-400" strokeWidth={1.5} />
            </div>
            <h2 className="text-2xl font-semibold mt-8 mb-2">{t('guard.allClear')}</h2>
            <p className="text-muted-foreground text-center">{t('guard.monitoring')}<br />{t('guard.noThreats')}</p>
          </div>
        ) : !activeCarouselAlerts.length ? (
          // acknowledged only
          acknowledgedAlerts.length > 0 && (
            <div className="flex-1 flex flex-col items-center justify-center" onClick={() => setShowAcknowledgedStack(true)}>
              <div className="relative w-48 h-48" data-art-center>
                <div className="absolute inset-0 bg-red-500/20 rounded-full animate-ping" />
                <div className="absolute inset-0 bg-red-500/10 rounded-full animate-pulse" />
                <Shield className="w-48 h-48 text-red-600 dark:text-red-400" strokeWidth={1.5} />
                <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-red-800 dark:text-red-200 text-3xl font-bold">
                  {acknowledgedAlerts.length}
                </span>
              </div>
              <h2 className="text-2xl font-semibold mt-8 mb-2">{t('guard.alertsInProgress')}</h2>
              <Button onClick={() => setShowAcknowledgedStack(true)} variant="default" className="mt-4 bg-red-600 text-white">
                <Shield className="w-4 h-4 mr-2" />
                {t('guard.viewAlerts')} ({acknowledgedAlerts.length})
              </Button>
            </div>
          )
        ) : (
          // Carousel with activeCarouselAlerts
          <div className="flex-1 flex flex-col min-h-0">
            <div className="flex items-center justify-between mb-4 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Shield className="w-12 h-12 text-red-600 animate-pulse" />
                  <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                    {activeCarouselAlerts.length}
                  </span>
                </div>
                <h2 className="text-lg font-bold text-red-600">
                  {unacknowledgedAlerts.length > 0 ? t('guard.alert') : t('guard.acknowledgedAlerts')}
                </h2>
              </div>
              {isDemoMode && unacknowledgedAlerts.length > 0 && (
                <Button variant="outline" size="sm" onClick={demoClearAllAlerts}>
                  {t('guard.clearAll')}
                </Button>
              )}
              {activeCarouselAlerts === acknowledgedAlerts && (
                <Button variant="outline" size="sm" onClick={() => setShowAcknowledgedStack(false)}>
                  <ChevronLeft className="w-4 h-4 mr-1" /> {t('common.back')}
                </Button>
              )}
            </div>
            <div className="flex-1 min-h-0 relative h-full">
              {carouselError ? (
                // Fallback: list view of alerts
                <div className="space-y-4 h-full">
                  {activeCarouselAlerts.map((alert) => (
                    <AlertCard
                      key={alert.id}
                      alertData={alert}
                      onAcknowledge={handleAcknowledge}
                      onComplete={handleComplete}
                      onAccompliceClick={handleAccompliceClick}
                    />
                  ))}
                </div>
              ) : (
                <>
                  <div className="overflow-hidden h-full" ref={emblaRef}>
                    <div className="flex h-full">
                      {activeCarouselAlerts.map((alert) => (
                        <div key={alert.id} className="flex-[0_0_100%] min-w-0 px-1 h-full overflow-y-auto">
                          <AlertCard
                            alertData={alert}
                            onAcknowledge={handleAcknowledge}
                            onComplete={handleComplete}
                            onAccompliceClick={handleAccompliceClick}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                  {activeCarouselAlerts.length > 1 && (
                    <>
                      <button onClick={handlePrev} className="absolute left-0 top-1/2 -translate-y-1/2 p-1 bg-white/80 dark:bg-gray-800/80 rounded-full shadow z-10">
                        <ChevronLeft className="w-6 h-6" />
                      </button>
                      <button onClick={handleNext} className="absolute right-0 top-1/2 -translate-y-1/2 p-1 bg-white/80 dark:bg-gray-800/80 rounded-full shadow z-10">
                        <ChevronRight className="w-6 h-6" />
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
            {activeCarouselAlerts.length > 1 && (
              <div className="flex justify-center gap-2 mt-4 flex-shrink-0">
                {activeCarouselAlerts.map((_, idx) => (
                  <button
                    key={idx}
                    className={cn("h-2 rounded-full transition-all", idx === selectedIndex ? "bg-red-600 w-6" : "bg-gray-300 w-2")}
                    onClick={() => emblaApi?.scrollTo(idx)}
                  />
                ))}
              </div>
            )}
          </div>
        )
      ) : (
        // ---- FACE LIST VIEW (with filters) ----
        <div className="space-y-4">
          <h2 className="text-xl font-bold">{t('guard.recentFacestime')}</h2>
          <p className="text-sm text-muted-foreground">{t('guard.recentDescription')}</p>

          {/* Filters – default to last 10 minutes */}
          <div className="flex flex-wrap gap-2 items-end">
            <div>
              <label className="text-xs">{t('guard.filterDate')}</label>
              <input
                type="date"
                value={faceFilterDate}
                onChange={(e) => setFaceFilterDate(e.target.value)}
                className="w-full p-1 border rounded text-sm"
              />
            </div>
            <div>
              <label className="text-xs">{t('guard.filterHour')}</label>
              <select
                value={faceFilterHour ?? ''}
                onChange={(e) => setFaceFilterHour(e.target.value ? parseInt(e.target.value) : null)}
                className="w-full p-1 border rounded text-sm"
              >
                <option value="">All</option>
                {Array.from({length: 24}, (_, i) => (
                  <option key={i} value={i}>{i.toString().padStart(2, '0')}:00</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs">{t('guard.filterMinBlock')}</label>
              <select
                value={faceFilterMinBlock ?? ''}
                onChange={(e) => setFaceFilterMinBlock(e.target.value ? parseInt(e.target.value) : null)}
                className="w-full p-1 border rounded text-sm"
              >
                <option value="">All</option>
                <option value="0">0‑9 min</option>
                <option value="1">10‑19 min</option>
                <option value="2">20‑29 min</option>
                <option value="3">30‑39 min</option>
                <option value="4">40‑49 min</option>
                <option value="5">50‑59 min</option>
              </select>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const now = new Date();
                setFaceFilterDate(localDayKey(now));
                setFaceFilterHour(now.getHours());
                setFaceFilterMinBlock(Math.floor(now.getMinutes() / 10));
                setFaceFilterType('all');
              }}
            >
              {t('common.clear')}
            </Button>
          </div>

          {/* Live mode: real local captures */}
          {!isDemoMode && (
            <>
              {showLocalCaptures ? (
                isLocalNetwork ? (
                  localCaptures.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {localCaptures
                        .filter((cap: any) => {
                          const capDate = new Date(cap.timestamp);
                          // Date filter
                          if (faceFilterDate) {
                            const capDateStr = localDayKey(capDate);
                            if (capDateStr !== faceFilterDate) return false;
                          }
                          // Hour filter
                          if (faceFilterHour !== null && capDate.getHours() !== faceFilterHour) return false;
                          // Minute block filter (0-9,10-19,...)
                          if (faceFilterMinBlock !== null) {
                            const mins = capDate.getMinutes();
                            const block = Math.floor(mins / 10);
                            if (block !== faceFilterMinBlock) return false;
                          }
                          // Type filter (role from edge)
                          if (faceFilterType === 'person' && cap.role !== 'person') return false;
                          if (faceFilterType === 'vehicle' && cap.role !== 'vehicle') return false;
                          return true;
                        })
                        .map((cap: any) => {
                          // v0.4.9.1.15: 3-stage HTTPS (hostname) → HTTPS (IP) →
                          // HTTP (IP) fallback chain. See api.ts → buildEdgeImageSrc
                          // for the contract. Works on APK once the cert is trusted.
                          const imgSrc = buildEdgeImageSrc(
                            { hostname: edgeHostname, edgeIp, edgePort },
                            `/captures/${cap.id}`
                          );
                          return (
                          <div key={cap.id} className="border rounded-lg p-3 bg-card">
                            <img
                              src={imgSrc.primary}
                              className="w-full h-48 object-contain rounded-md"
                              alt="Capture"
                              onError={(e) => {
                                // v0.4.9.1.15: walk primary → ipHttps → fallback → placeholder
                                const cur = e.currentTarget.src;
                                if (imgSrc.ipHttps && cur !== imgSrc.ipHttps) {
                                  e.currentTarget.src = imgSrc.ipHttps;
                                  return;
                                }
                                if (imgSrc.fallback && cur !== imgSrc.fallback) {
                                  e.currentTarget.src = imgSrc.fallback;
                                  return;
                                }
                                e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"%3E%3Crect x="2" y="2" width="20" height="20" rx="2" ry="2"%3E%3C/rect%3E%3Cpath d="M7 2v20M17 2v20M2 12h20M2 7h5M2 17h5M17 17h5M17 7h5"%3E%3C/path%3E%3C/svg%3E';
                              }}
                            />
                            <div className="text-xs text-muted-foreground mt-1">
                              <span className="font-medium">Camera:</span> {cap.camera_name || 'Unknown'}
                            </div>
                            <div className="mt-2">
                              <span className="text-xs text-muted-foreground">
                                {new Date(cap.timestamp).toLocaleString()}
                              </span>
                              {/* flex-wrap, not a fixed two-row layout: the
                                  buttons reflow on their own, so a narrow
                                  phone wraps them instead of pushing them off
                                  the edge where they cannot be tapped. The
                                  timestamp moves above for the same reason. */}
                              <div className="flex flex-wrap gap-1 mt-1">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleAddToPoiClick(cap)}
                                  disabled={addingToPoi}
                                >
                                  <PlusCircle className="w-3 h-3 mr-1" /> Add to POI
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleAddToAuthClick(cap)}
                                  disabled={addingToAuth}
                                >
                                  <Users className="w-3 h-3 mr-1" /> {t('guard.addToAuthorized')}
                                </Button>
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedCaptureForFlag(cap);
                                    setFlagCaptureForm({ sex: '', threatLevel: '', notes: '' });
                                  }}
                                >
                                  <Flag className="w-3 h-3 mr-1" /> {t('guard.flagAsPOI')}
                                </Button>
                              </div>
                            </div>
                          </div>
                          );
                        })}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No captures match the filters.</p>
                  )
                ) : (
                  <p className="text-sm text-muted-foreground">{t('guard.notOnLocalNetwork')}</p>
                )
              ) : (
                <p className="text-sm text-muted-foreground">
                  Click the “Recent Faces” button to load captures from the local edge server.
                </p>
              )}
            </>
          )}

          {/* Demo mode: random faces with flagging */}
          {isDemoMode && (() => {
            const filteredFaces = demoRecentFaces.filter(face => {
              if (faceFilterDate) {
                const faceDate = localDayKey(face.timestamp);
                if (faceDate !== faceFilterDate) return false;
              }
              if (faceFilterHour !== null && face.timestamp.getHours() !== faceFilterHour) return false;
              if (faceFilterMinBlock !== null) {
                const mins = face.timestamp.getMinutes();
                const block = Math.floor(mins / 10);
                if (block !== faceFilterMinBlock) return false;
              }
              if (faceFilterType === 'person' && face.sex === 'V') return false;
              if (faceFilterType === 'vehicle' && face.sex !== 'V') return false;
              return true;
            });

            if (filteredFaces.length === 0) {
              return <p className="text-sm text-muted-foreground">{t('guard.noFacesMatch')}</p>;
            }

            return (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredFaces.map(face => (
                  <div key={face.id} className="border rounded-lg p-3 bg-card">
                    <img src={face.imageUrl} className="w-full h-48 object-contain rounded-md" />
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-xs text-muted-foreground">
                        {face.timestamp.toLocaleString()}
                      </span>
                      {face.sex && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-muted">
                          {face.sex === 'V' ? t('flag.sex.vehicle') : t('flag.sex.person')}
                        </span>
                      )}
                    </div>
                    <div className="flex gap-1 mt-2 flex-wrap">
                      <Button variant="outline" size="sm" onClick={() => handleAddToPoiClick(face)}>
                        <PlusCircle className="w-3 h-3 mr-1" /> {t('duplicates.addToPOI') || 'Add to POI'}
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleAddToAuthClick(face)}>
                        <Users className="w-3 h-3 mr-1" /> Add to Authorized
                      </Button>
                      <Button variant="destructive" size="sm" onClick={() => setSelectedFace(face)}>
                        <Flag className="w-3 h-3 mr-1" /> {t('duplicates.flagAsPOI') || 'Flag as POI'}
                      </Button>
                    </div>
                    {selectedFace?.id === face.id && (
                      <div className="mt-3 space-y-2">
                        <label className="block text-sm mb-1">{t('flag.sex.title')}</label>
                        <select
                          className="w-full border rounded p-1 text-sm"
                          value={flagFormData.sex}
                          onChange={e => setFlagFormData({...flagFormData, sex: e.target.value})}
                        >
                          <option value="" disabled>{t('flag.sex.select')}</option>
                          <option value="F">{t('flag.sex.female')}</option>
                          <option value="M">{t('flag.sex.male')}</option>
                          <option value="V">{t('flag.sex.vehicle')}</option>
                        </select>
                        <label className="block text-sm mb-1">{t('threat.title')}</label>
                        <select
                          className="w-full border rounded p-1 text-sm"
                          value={flagFormData.threatLevel}
                          onChange={e => setFlagFormData({...flagFormData, threatLevel: e.target.value as any})}
                        >
                          <option value="" disabled>{t('threat.select')}</option>
                          <option value="Low">{t('threat.low')}</option>
                          <option value="Medium">{t('threat.medium')}</option>
                          <option value="High">{t('threat.high')}</option>
                        </select>
                        <label className="block text-sm mb-1">{t('flag.notes')}</label>
                        <textarea
                          placeholder={t('flag.notes')}
                          className="w-full border rounded p-1 text-sm"
                          rows={2}
                          value={flagFormData.notes}
                          onChange={e => setFlagFormData({...flagFormData, notes: e.target.value})}
                        />
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => handleFlagSubmit(face.id)}>
                            <Flag className="w-3 h-3 mr-1" /> {t('flag.confirm')}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setSelectedFace(null)}>
                            {t('common.cancel')}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      )
    )}
    </main>

    <GuardFooter
      activeStoreName={activeStoreName}
      hasAlerts={hasAlerts}
      viewMode={viewMode}
    />

    {selectedPoiForModal && (
      <POIDetailModal
        poi={selectedPoiForModal as any}
        onClose={() => setSelectedPoiForModal(null)}
        onEdit={() => {}}
        readOnly={true}
      />
    )}

    {/* --- Flag as POI modal (with duplicate check) --- */}
    {selectedCaptureForFlag && (
      <Dialog open={!!selectedCaptureForFlag} onOpenChange={() => setSelectedCaptureForFlag(null)}>
        <DialogContent className="bg-white dark:bg-gray-900">
          <DialogHeader>
            <DialogTitle>{t('admin.addPOI')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{t('flag.sex.title')}</Label>
              <select
                className="w-full border rounded p-2 bg-background"
                value={flagCaptureForm.sex}
                onChange={e => setFlagCaptureForm({ ...flagCaptureForm, sex: e.target.value })}
              >
                <option value="">{t('flag.sex.select')}</option>
                <option value="F">{t('flag.sex.female')}</option>
                <option value="M">{t('flag.sex.male')}</option>
                <option value="V">{t('flag.sex.vehicle')}</option>
              </select>
            </div>
            <div>
              <Label>{t('threat.title')}</Label>
              <select
                className="w-full border rounded p-2 bg-background"
                value={flagCaptureForm.threatLevel}
                onChange={e => setFlagCaptureForm({ ...flagCaptureForm, threatLevel: e.target.value })}
              >
                <option value="">{t('threat.select')}</option>
                <option value="Low">{t('threat.low')}</option>
                <option value="Medium">{t('threat.medium')}</option>
                <option value="High">{t('threat.high')}</option>
              </select>
            </div>
            <div>
              <Label>{t('flag.notes')}</Label>
              <textarea
                className="w-full border rounded p-2 bg-background"
                rows={2}
                value={flagCaptureForm.notes}
                onChange={e => setFlagCaptureForm({ ...flagCaptureForm, notes: e.target.value })}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => {
                setSelectedCaptureForFlag(null);
                setFlagCaptureForm({ sex: '', threatLevel: '', notes: '' });
              }}>
                {t('common.cancel')}
              </Button>
              <Button onClick={() => handleFlagCaptureSubmit(selectedCaptureForFlag)}>
                {t('flag.confirm')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    )}

    {/* --- Duplicate Action Modal (when flagging as POI and duplicate found) --- */}
    {duplicateActionModal && (
      <Dialog open={!!duplicateActionModal} onOpenChange={() => setDuplicateActionModal(null)}>
        <DialogContent className="bg-white dark:bg-gray-900">
          <DialogHeader>
            <DialogTitle>{t('duplicates.checkDuplicates') || 'Duplicate POI Detected'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p>{t('duplicates.possibleFound') || 'A similar POI already exists. What would you like to do?'}</p>
            <div className="flex gap-4">
              <div className="border p-2 rounded flex-1">
                <p className="text-xs font-semibold">{t('duplicates.existing') || 'Existing POI'}</p>
                <img 
                  src={resolveImageUrl(duplicateActionModal.duplicates[0].photoUrl)} 
                  className="w-full h-auto rounded border" 
                  onError={(e) => { e.currentTarget.src = '/images/default-avatar.png'; }}
                />
                <p className="text-sm mt-1">{duplicateActionModal.duplicates[0].code}</p>
                {/* Optional: show similarity score */}
                {duplicateActionModal.duplicates[0].similarity && (
                  <p className="text-xs text-muted-foreground">
                    Match confidence: {(duplicateActionModal.duplicates[0].similarity * 100).toFixed(1)}%
                  </p>
                )}
              </div>
              <div className="border p-2 rounded flex-1">
                <p className="text-xs font-semibold">{t('duplicates.new') || 'New Capture'}</p>
                {(() => {
                  // v0.4.9.1.15: 3-stage fallback chain (primary → ipHttps → fallback).
                  // See api.ts → buildEdgeImageSrc for the contract.
                  const imgSrc = buildEdgeImageSrc(
                    { hostname: edgeHostname, edgeIp, edgePort },
                    `/captures/${duplicateActionModal.capture.id}`
                  );
                  return (
                    <img
                      src={imgSrc.primary}
                      className="w-full h-auto rounded border"
                      onError={(e) => {
                        const cur = e.currentTarget.src;
                        if (imgSrc.ipHttps && cur !== imgSrc.ipHttps) {
                          e.currentTarget.src = imgSrc.ipHttps;
                          return;
                        }
                        if (imgSrc.fallback && cur !== imgSrc.fallback) {
                          e.currentTarget.src = imgSrc.fallback;
                        }
                      }}
                    />
                  );
                })()}
              </div>
            </div>
            <div className="flex flex-col gap-2">
              {/* v0.4.9.1.8: cross-network warning.
                  If the matched duplicate (Y) is from another network, the user
                  cannot add to it. Show a warning + disable the Y button. */}
              {duplicateActionModal.duplicates[0].crossNetwork && (
                <div className="text-xs text-amber-600 dark:text-amber-400 border border-amber-300 dark:border-amber-700 rounded p-2 bg-amber-50 dark:bg-amber-950/30">
                  {t('duplicates.crossNetworkWarning', { network: duplicateActionModal.duplicates[0].networkName })}
                </div>
              )}
              {/* v0.4.9.1.7 bug fix: if the user picked a POI from the search list
                  BEFORE the duplicate check fired, show that as an option too. */}
              {duplicateActionModal.selectedPoi && 
               duplicateActionModal.selectedPoi.id !== duplicateActionModal.duplicates[0].id && (
                <Button 
                  variant="outline"
                  onClick={async () => {
                    const targetPoi = duplicateActionModal.selectedPoi!;
                    setDuplicateActionModal(null);
                    await handleAddToPoiConfirm(targetPoi.id, { skipGlobalCheck: true });
                  }}
                >
                  <PlusCircle className="w-4 h-4 mr-2" /> {t('duplicates.addToMySelection', { code: duplicateActionModal.selectedPoi.code })}
                </Button>
              )}
              <Button 
                onClick={async () => {
                  const existingPoi = duplicateActionModal.duplicates[0];
                  // Close the duplicate modal immediately
                  setDuplicateActionModal(null);
                  // Then add to POI (will also close addToPoiModal on success)
                  await handleAddToPoiConfirm(existingPoi.id, { skipGlobalCheck: true });
                 }}
                disabled={duplicateActionModal.duplicates[0].crossNetwork}
              >
                <PlusCircle className="w-4 h-4 mr-2" /> {t('duplicates.addToExistingDuplicate')}
              </Button>
              <Button 
                variant="outline"
                onClick={() => {
                  // Create new POI
                  const capture = duplicateActionModal.capture;
                  const embedding = duplicateActionModal.embedding;
                  createPOIFromCapture(capture, embedding);
                  setDuplicateActionModal(null);
                }}
              >
                {t('duplicates.addAsNewEitherWay')}
              </Button>
              <Button variant="outline" onClick={() => setDuplicateActionModal(null)}>
                {t('common.cancel')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    )}

    {/* --- Add to POI Modal --- */}
    {addToPoiModal && (
      <Dialog open={!!addToPoiModal} onOpenChange={() => setAddToPoiModal(null)}>
        <DialogContent className="bg-white dark:bg-gray-900 max-w-md">
          <DialogHeader>
            <DialogTitle>Add to Existing POI</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Search POI</Label>
              <Input
                placeholder="Type POI code (e.g. ES-MAD-12345)"
                value={poiSearchQuery}
                onChange={(e) => {
                  const query = e.target.value;
                  setPoiSearchQuery(query);
                  if (query.length >= 3) {
                    const filtered = poiSearchResults.filter(p => 
                      p.code.toLowerCase().includes(query.toLowerCase())
                    );
                    setPoiSearchResults(filtered);
                  } else {
                    setPoiSearchResults([]);
                  }
                }}
              />
            </div>
            <div className="max-h-60 overflow-y-auto border rounded divide-y">
              {poiSearchResults.length === 0 && (
                <p className="p-3 text-sm text-muted-foreground">
                  {poiSearchQuery.length >= 3 ? 'No POIs found' : 'Type at least 3 characters to search'}
                </p>
              )}
              {poiSearchResults.map(poi => (
                <div
                  key={poi.id}
                  className="flex items-center gap-3 p-3 cursor-pointer hover:bg-muted/50"
                  onClick={() => handleAddToPoiConfirm(poi.id)}
                >
                  <img src={poi.photoUrl} className="w-10 h-10 rounded-full object-cover" />
                  <div>
                    <p className="font-mono text-sm font-medium">{poi.code}</p>
                    <p className="text-xs text-muted-foreground">{poi.threatLevel}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    )}

    {addToAuthModal && (
      <Dialog open={!!addToAuthModal} onOpenChange={() => setAddToAuthModal(null)}>
        <DialogContent className="bg-white dark:bg-gray-900 max-w-md">
          <DialogHeader>
            <DialogTitle>{t('guard.addToAuthorizedTitle')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <img
                src={addToAuthModal.photoUrl}
                alt=""
                className="w-14 h-14 rounded object-cover"
                // Same ladder as every other capture tile. Without it the
                // preview is a broken-image icon on store WiFi, which is
                // exactly where a guard needs to SEE who they are adding.
                onError={(e) => {
                  const cur = e.currentTarget.src;
                  const s = buildEdgeImageSrc(
                    { hostname: edgeHostname, edgeIp, edgePort },
                    `/captures/${addToAuthModal.capture.id}`
                  );
                  if (s.ipHttps && cur !== s.ipHttps) { e.currentTarget.src = s.ipHttps; return; }
                  if (s.fallback && cur !== s.fallback) { e.currentTarget.src = s.fallback; return; }
                }}
              />
              <p className="text-xs text-muted-foreground">{t('guard.addToAuthorizedBody')}</p>
            </div>
            <div>
              <Label>{t('guard.searchPerson')}</Label>
              <Input
                placeholder={t('guard.typeNameToSearch')}
                value={authSearchQuery}
                onChange={(e) => {
                  const q = e.target.value;
                  setAuthSearchQuery(q);
                  const all = authSearchResults;
                  setAuthSearchResults(
                    q.length === 0 ? all : all.filter((p) => (p.name || '').toLowerCase().includes(q.toLowerCase()))
                  );
                }}
              />
            </div>
            <div className="max-h-60 overflow-y-auto border rounded divide-y">
              {authSearchResults.length === 0 && (
                <p className="p-3 text-sm text-muted-foreground">
                  {authSearchQuery ? t('guard.noMatchingPerson') : t('guard.typeNameToSearchEmpty')}
                </p>
              )}
              {authSearchResults.map((p) => {
                // Gallery room is per person. The server enforces 20 on POST
                // (stores.js), so show it here rather than letting the guard
                // click through to an error.
                const used = typeof p.image_count === 'number' ? p.image_count : 0;
                const full = used >= 20;
                return (
                <div
                  key={p.id}
                  className={`flex items-center gap-3 p-3 ${full ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-muted/50'}`}
                  onClick={() => { if (!addingToAuth && !full) handleAddToAuthConfirm(p.id); }}
                >
                  {p.photo_url ? (
                    <img src={p.photo_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-xs">N/A</div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{p.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {p.role && <span>{p.role}</span>}
                      <span className="ml-2">{t('guard.galleryUsage', { used })}</span>
                    </p>
                  </div>
                  {full && <span className="text-[10px] text-amber-600 dark:text-amber-400 font-medium">{t('guard.galleryFull')}</span>}
                </div>
                );
              })}
            </div>
            {addingToAuth && <p className="text-xs text-muted-foreground">{t('guard.adding')}</p>}
          </div>
        </DialogContent>
      </Dialog>
    )}

    {/* --- Duplicate modal for existing flow (legacy, kept for compatibility) --- */}
    {duplicatePOI && pendingCapture && pendingEmbedding && (
      <Dialog open={!!duplicatePOI} onOpenChange={() => {
        setDuplicatePOI(null);
        setPendingCapture(null);
        setPendingEmbedding(null);
      }}>
        <DialogContent className="bg-white dark:bg-gray-900">
          <DialogHeader>
            <DialogTitle>{t('duplicates.checkDuplicates') || 'Duplicate POI Detected'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p>{t('duplicates.similarInNetwork') || 'A similar POI already exists. Compare the images below:'}</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="font-semibold mb-1">{t('duplicates.existing') || 'Existing POI'}</p>
                <img src={duplicatePOI.photoUrl} className="w-full h-auto rounded border" />
                <p className="text-sm mt-1">{duplicatePOI.code}</p>
              </div>
              <div>
                <p className="font-semibold mb-1">{t('duplicates.new') || 'New Capture'}</p>
                {(() => {
                  // v0.4.9.1.15: 3-stage fallback chain (primary → ipHttps → fallback).
                  // See api.ts → buildEdgeImageSrc for the contract.
                  const imgSrc = buildEdgeImageSrc(
                    { hostname: edgeHostname, edgeIp, edgePort },
                    `/captures/${pendingCapture.id}`
                  );
                  return (
                    <img
                      src={imgSrc.primary}
                      className="w-full h-auto rounded border"
                      onError={(e) => {
                        const cur = e.currentTarget.src;
                        if (imgSrc.ipHttps && cur !== imgSrc.ipHttps) {
                          e.currentTarget.src = imgSrc.ipHttps;
                          return;
                        }
                        if (imgSrc.fallback && cur !== imgSrc.fallback) {
                          e.currentTarget.src = imgSrc.fallback;
                        }
                      }}
                    />
                  );
                })()}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => {
                setDuplicatePOI(null);
                setPendingCapture(null);
                setPendingEmbedding(null);
              }}>
                {t('common.cancel')}
              </Button>
              <Button onClick={() => {
                if (pendingCapture && pendingEmbedding) {
                  createPOIFromCapture(pendingCapture, pendingEmbedding);
                }
              }}>
                {t('duplicates.addAsNewEitherWay') || 'Create Anyway'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    )}

  </div>
);
}